import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { cachedGet } from './catalogueCache';
//  The patient's other attendances, opened from Entries ▸ Past History. It reuses
//  the consulting room's visit-summary endpoint and its per-visit record dialog,
//  so the claims screen and the clinical screen cannot show different histories.
import PastHistoryDialog from './PastHistoryDialog';
//  Entries ▸ Documentation. The four out-patient notes, written to the same place
//  the consulting room writes them, by the same endpoint.
import DocumentationDialog from './DocumentationDialog';
//  What NHIA said about this claim, read back from an imported portal report.
//  Renders nothing when there is no verdict — see the component's header.
import ClaimVerdictPanel from './ClaimVerdictPanel';
import {
  Users, Activity, ChevronDown, Plus, Pencil, Save,
  Stethoscope, FlaskConical, Pill, FileText, History,
  BarChart3, SquarePen,
  //  Documentation's mark. Distinct from FileText, which Diagnosis already uses —
  //  two entries in one short menu sharing an icon is the menu telling you they
  //  are the same kind of thing.
  ClipboardList,
  ChevronLeft, ChevronRight, X, AlertTriangle, ShieldAlert, Wrench, Loader2,
} from 'lucide-react';
import {
  claimStoreDetail, claimStoreBio, claimStoreBioPreview, claimStoreServices,
  claimStoreRepair, insurancetypes, claimStoreDocStatus,
  //  Choosing what a long admission is billed on. Writes the procedure or
  //  diagnosis row AND the specialty in one call — see saveBio.
  claimStorePrincipal,
  //  The specialty fix applied when a hospital claim is opened. Separate from
  //  claimStoreRepair, which cannot act on a flat claim at all.
  claimStoreSettleSpecialty,
  //  One claim's STORED clinical review. Never runs the model — the scan is a
  //  month-wide job started from the claims list. See ClinicalReviewNote below.
  claimAiReviewOf,
} from '../BaseURL';

import { BRAND } from '../../brand';
import ClaimProcedures from './ClaimProcedures';
import ClaimDiagnoses from './ClaimDiagnoses';
//  The dropdown you can type into. Four other panels in this editor already use
//  it, so a code found in the Service picker behaves like a code found in the
//  Diagnoses or Procedures list — same matching, same ranking, same keys.
import SearchSelect from './SearchSelect';
//  Put a whole condition template onto this claim — the four sections at once,
//  judged against THIS patient's age, gender and attendance before anything is
//  written. Folded away until asked for; see the header of that file.
import ClaimTemplateApply from './ClaimTemplateApply';
import ClaimInvestigations from './ClaimInvestigations';
import ClaimMedications from './ClaimMedications';
import { confirmClaim, notifyClaim } from './claimsDialog';

//  ── THE DOCUMENTATION MARKER ───────────────────────────────────────────────
//  Three marks on the Entries row, so an officer can see what is outstanding
//  without opening the dialog on every claim in a list of thousands.
//
//  One combined count would say nothing useful — "2 of 3" does not tell you
//  whether the notes or the lab results are the part still missing, which is the
//  only thing the row is being read for.
//
//  Labs carry a fraction rather than a tick because part-way is the normal state
//  on a claim with several tests, and a tick would round it up to finished.
const docMarks = (s) => {
  const marks = [{ key: 'notes', label: 'N', done: !!s.notes?.any }];
  if (s.labs?.requested > 0) {
    marks.push({
      key: 'labs',
      label: `L ${s.labs.with_results}/${s.labs.requested}`,
      done: s.labs.with_results >= s.labs.requested,
    });
  }
  //  Vitals are null on an admission: observations there are a chart taken on
  //  every ward round, and a single tick for a fifty-night stay would say
  //  something untrue. No answer means NO MARK, not a hollow one.
  if (s.vitals) marks.push({ key: 'vitals', label: 'V', done: !!s.vitals.any });
  return marks;
};

//  The hover text. The marks are deliberately terse, so this is where the row
//  says what it actually means — including WHICH notes are missing, which is the
//  difference between "there is work here" and knowing what the work is.
const docStatusTitle = (s) => {
  const lines = [];
  lines.push(s.notes?.any
    ? `Notes: ${s.notes.filled.join(', ')}`
      + (s.notes.missing?.length
        ? ` — still to write: ${s.notes.missing.join(', ')}`
        : ' — all four written')
    : 'Notes: nothing written yet');
  if (s.labs?.requested > 0) {
    lines.push(`Lab results: ${s.labs.with_results} of ${s.labs.requested} entered`
      + (s.labs.unmatched ? ` (${s.labs.unmatched} with no laboratory form)` : ''));
  }
  if (s.vitals) {
    lines.push(s.vitals.any
      ? `Vitals taken: ${s.vitals.taken.join(', ')}`
      //  A vitals row is opened at registration and filled at triage, so an empty
      //  one is a real state — and a different one from never being seen.
      : s.vitals.row_exists
        ? 'Vitals: a record was opened but no observations were entered'
        : 'Vitals: not taken at this visit');
  }
  if (!s.keyed) lines.push('This claim has no folder number or visit date to file against.');
  return lines.join('\n');
};

// ============================================================================
//  ClaimEditor — one claim, opened inline underneath its row
// ============================================================================
//
//  Deliberately NOT a dialog or a new tab. An officer works a month of claims in
//  sequence; a modal means dismiss-scroll-find-your-place on every single one.
//  This expands in place and collapses back, so the row you were on is still
//  where you left it.
//
//  Everything comes from ONE request — /claims/store/detail — because the panel
//  opens on a click and five separate spinners would make a fast thing feel slow.
//  It works for either claim model: a flat claim is addressed by the composite
//  'patientId::date' and an imported one by its row id, and the response envelope
//  is identical either way, so nothing here branches on which.
//
//  WHAT IS WIRED: reading a claim in full, and EDITING the two header cards —
//  Member Details and Service & dates. One Edit puts both into edit mode against a
//  single draft, and Save writes them through PUT /claims/store/bio, which
//  whitelists what it will accept. The screen then adopts the RE-READ claim rather
//  than the draft, so what appears is what the database took.
//
//  Two fields reach beyond their own column and the confirmation says so, from
//  /claims/store/bio/preview:
//
//    the visit date    decides which MONTH the claim is counted in, so editing it
//                      moves the claim out of the month on screen
//    the folder number on a TYPED-IN claim, its medications and diagnoses are
//                      matched on this value, so the rename cascades to them
//
//  WHAT IS NOT: the child tables. Add / Edit / Delete on procedures, diagnoses,
//  investigations and medications are present and inert, and say so when clicked.
//  Those need their own write path; a form that silently discarded what you typed
//  would be worse than one that admits it is read-only.

// ── One tone per section ────────────────────────────────────────────────────
//  Procedures, Diagnoses, Investigations and Medications were four identical
//  cards stacked on each other. Scrolling a long claim, the only thing telling
//  you which table you were typing into was the word at the top of it — so a
//  medication went in under Diagnoses easily enough.
//
//  Deliberately QUIET. `wash` is a 3–5% tint, barely a shade off white; the tone
//  carries on the 3px left edge and the icon, where the eye picks it up in
//  peripheral vision without the page turning into four coloured boxes. All four
//  sit at the same saturation and lightness so they read as one family.
//
//  Colour is never the only difference: every section already has its own ICON
//  and its own TITLE. That matters beyond taste — the palette's blue and violet
//  are close under deuteranopia, and a section identified by hue alone would be
//  a section a red-green colourblind officer cannot tell apart.
const TONE = {
  procedures:     { line: '#1A9B7F', ink: '#0F6E5A', wash: '#F2F8F6' },  // teal
  diagnoses:      { line: '#3A6EA5', ink: '#2A5480', wash: '#F2F6FB' },  // blue
  investigations: { line: '#6C5CA8', ink: '#544399', wash: '#F5F3FB' },  // violet
  medications:    { line: '#B07C2E', ink: '#8A5F1E', wash: '#FBF7F0' },  // ochre
};
const NEUTRAL_TONE = { line: BRAND.green, ink: BRAND.green, wash: BRAND.surface };

const G = {
  card: {
    background: BRAND.surface, border: `1px solid ${BRAND.line}`,
    borderLeft: `3px solid ${BRAND.green}`, borderRadius: 10,
  },
  cardHead: {
    display: 'flex', alignItems: 'center', gap: 10, padding: '11px 14px',
    borderBottom: `1px solid ${BRAND.line}`,
  },
  cardIcon: {
    width: 28, height: 28, borderRadius: 8, flexShrink: 0,
    display: 'grid', placeItems: 'center',
    background: BRAND.navySoft, color: BRAND.navy,
  },
  cardTitle: { fontSize: 13, fontWeight: 800, color: BRAND.ink, lineHeight: 1.2 },
  cardSub: { fontSize: 11, color: BRAND.inkFaint, marginTop: 1 },
  editBtn: {
    display: 'inline-flex', alignItems: 'center', gap: 5, flexShrink: 0,
    padding: '5px 11px', borderRadius: 7, font: 'inherit', fontSize: 11.5,
    fontWeight: 700, cursor: 'pointer',
    border: `1px solid ${BRAND.line}`, background: BRAND.surface, color: BRAND.inkMuted,
  },
  field: {
    width: '100%', boxSizing: 'border-box', padding: '8px 10px',
    fontSize: 12.5, font: 'inherit', fontFamily: 'inherit',
    border: `1px solid ${BRAND.line}`, borderRadius: 7,
    background: BRAND.surface, color: BRAND.ink,
    minHeight: 34,
  },
  fold: {
    display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px',
    background: '#EEF2F8', border: `1px solid ${BRAND.line}`,
    borderLeft: `3px solid ${BRAND.green}`, borderRadius: 9,
  },
  addBtn: {
    display: 'inline-flex', alignItems: 'center', gap: 5, flexShrink: 0,
    padding: '6px 13px', borderRadius: 7, font: 'inherit', fontSize: 12,
    fontWeight: 700, cursor: 'pointer',
    border: `1px solid ${BRAND.green}`, background: BRAND.green, color: '#fff',
  },
  sideCard: {
    background: BRAND.surface, border: `1px solid ${BRAND.line}`, borderRadius: 10,
    overflow: 'hidden',
  },
  sideHead: {
    padding: '9px 13px', fontSize: 12, fontWeight: 800, color: BRAND.ink,
    background: '#F7F9FC', borderBottom: `1px solid ${BRAND.line}`,
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
  },
  th: {
    textAlign: 'left', padding: '8px 10px', fontSize: 9.5, fontWeight: 800,
    letterSpacing: '.07em', textTransform: 'uppercase', color: BRAND.inkMuted,
    background: '#F7F9FC', borderBottom: `1px solid ${BRAND.line}`, whiteSpace: 'nowrap',
  },
  td: { padding: '9px 10px', borderBottom: `1px solid ${BRAND.line}`, verticalAlign: 'middle' },
  ghost: {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
    width: '100%', padding: '7px 10px', borderRadius: 7, font: 'inherit',
    fontSize: 11.5, fontWeight: 700, cursor: 'pointer',
    border: `1px solid ${BRAND.line}`, background: BRAND.surface, color: BRAND.navy,
  },
};

// Buttons on the navy identity bar. Disabled at the ends of the month rather than
// hidden, so the control does not move about as you step through.
const barBtn = (disabled) => ({
  display: 'inline-flex', alignItems: 'center', gap: 4,
  padding: '4px 11px', borderRadius: 7,
  border: '1px solid rgba(255,255,255,.35)', background: 'transparent',
  color: '#fff', font: 'inherit', fontSize: 11.5, fontWeight: 700,
  cursor: disabled ? 'not-allowed' : 'pointer',
  opacity: disabled ? .4 : 1,
  whiteSpace: 'nowrap',
});

const ghs = (v) => `GHS ${Number(v || 0).toLocaleString(undefined, {
  minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// "10/11/2010" — the format the design shows, from the 'YYYY-MM-DD' the API sends.
const dmy = (v) => {
  if (!v) return '';
  const [y, m, d] = String(v).slice(0, 10).split('-');
  return (y && m && d) ? `${d}/${m}/${y}` : String(v);
};

const ageText = (dob, on) => {
  if (!dob) return '';
  const a = new Date(dob); const b = on ? new Date(on) : new Date();
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return '';
  let mo = (b.getFullYear() - a.getFullYear()) * 12 + (b.getMonth() - a.getMonth());
  if (b.getDate() < a.getDate()) mo -= 1;
  if (mo < 0) return '';
  return `${Math.floor(mo / 12)} years, ${mo % 12} months`;
};

//  NHIA attendance code -> what to show. CHR was missing, so the one attendance
//  the doctor's panel writes most often rendered as the bare code "CHR" in read
//  mode while every other code got a label.
//
//  CWC / FPL / OTH can only arrive from an XML import — the dropdown below does
//  not offer them, and claimStore.ATTENDANCE_CODES does not list them either, so
//  re-saving attendance on such a claim drops the value. Labelled here so it at
//  least READS correctly.
const ATTENDANCE_LABEL = {
  CHR: 'Chronic follow-ups',
  EAE: 'Emergency/Acute', ANC: 'Antenatal', PNC: 'Postnatal',
  CWC: 'Child Welfare', FPL: 'Family Planning', OTH: 'Other',
};
const cap = (v) => (v ? String(v).charAt(0).toUpperCase() + String(v).slice(1).replace(/_/g, ' ') : '');

//  The editable bio fields, in the words the form uses for them. The save
//  confirmation names what is changing, and `surname` or `is_dependant` is the
//  column name rather than the label on the field the officer just typed into.
const BIO_LABEL = {
  scheme_name: 'Insurance company',
  member_no: 'Membership no.',
  folder_no: 'Folder number',
  policy_option: 'Insurance policy number',
  surname: 'Surname',
  other_names: 'Other names',
  gender: 'Gender',
  date_of_birth: 'Date of birth',
  ccc: 'CCC no',
  is_dependant: 'Dependant',
  service_code: 'Service',
  specialty_code: 'Specialty code',
  specialty_name: 'Specialty description',
  attendance_code: 'Type of attendance',
  attendance_type: 'Attendance',
  outcome: 'Service outcome',
  visit_date: 'Visit date',
  discharge_date: 'Discharge date',
};

//  One line of the save confirmation: the field, what it held, what it will
//  hold. "Discharge date  30/06/2026 → 02/07/2026" answers the question the
//  officer actually has, which "4 changes" never did.
const describeChange = (key, header, draft) => {
  //  ── THE PRINCIPAL IS AN OBJECT, NOT A COLUMN ──────────────────────────────
  //
  //  `principal_pick` carries the whole chosen entry — its kind, its ICD-10, its
  //  tariff — because the save needs all of it. Run through String() like every
  //  other field it read "principal pick: empty → [object Object]", which told
  //  the officer nothing and looked like a fault in the middle of a confirmation
  //  they were being asked to trust.
  //
  //  It is also the single most consequential line in that list: it is the one
  //  that adds a row to the claim's own procedures or diagnoses and marks it the
  //  principal. So it is spelled out rather than merely made safe.
  if (key === 'principal_pick') {
    const p = draft?.principal_pick;
    if (!p) return 'Principal G-DRG:  cleared';
    const noun = p.kind === 'procedure' ? 'procedure' : 'diagnosis';
    const icd = p.kind === 'diagnosis' && p.icd10 ? `, ICD-10 ${p.icd10}` : '';
    return `Principal G-DRG:  ${p.name || p.code} (${p.code})${icd}`
         + ` — added to this claim's ${noun}s and starred as the principal`;
  }
  const label = BIO_LABEL[key] || key.replace(/_/g, ' ');
  const shown = (v) => {
    if (key === 'is_dependant') return v ? 'Yes' : 'No';
    //  Anything that is not a scalar is a draft field like the one above, and
    //  must never reach String() — "[object Object]" in a confirmation dialog is
    //  worse than the field being left out of it.
    if (v && typeof v === 'object') return 'set';
    const s = String(v ?? '').trim();
    if (!s) return 'empty';
    if (key === 'attendance_code') return ATTENDANCE_LABEL[s] || s;
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return dmy(s);
    return s;
  };
  return `${label}:  ${shown(header?.[key])} → ${shown(draft?.[key])}`;
};

//  The prescription line moved to ClaimMedications with the table that renders it.

// ── Read-only field ─────────────────────────────────────────────────────────
//  A real input, but readOnly. Rendering these as plain text would look right and
//  then surprise someone who clicked expecting to type; readOnly with the panel's
//  Edit button inert is at least consistent about what it does.
//  `hintTone` colours the note. `hintWarn` is the original boolean and still
//  works — it means 'warn' — because five call sites use it and a rename would
//  have been a change with no reader-visible benefit.
//
//  'ok' and 'busy' arrived with the automatic specialty fix: a field that has just
//  been CORRECTED must not explain itself in the same amber as one that is still
//  wrong, or the officer reads "something is off here" about a value the system
//  has already settled — which is the whole complaint that fix answers.
const HINT_TONES = {
  warn: { color: '#8A5510', fontWeight: 600 },
  ok: { color: '#166534', fontWeight: 600 },
  busy: { color: '#1E4E8C', fontWeight: 600 },
};

// ── What the clinical review said about THIS claim ──────────────────────────
//
//  ASKED FOR: "on these claims it is a warning at the top, just like you show the
//   errors and then those things."
//
//  Reads the STORED answer — the review runs as a month-wide job from the claims
//  list, never on opening a claim. Opening 2,264 claims would mean 2,264 calls to
//  a model, and an officer stepping through a month would pay for every one of
//  them twice. So this asks the database what was already found and renders
//  nothing at all when the claim has not been reviewed, or was and came back
//  clean: a permanent "nothing found" strip is noise that trains people to skip
//  the space where the real warnings appear.
//
//  ── IT NAMES THE ROW, AND IT NEVER OFFERS TO FIX ───────────────────────────
//
//  Every finding points at a row that is actually on the claim — the server drops
//  anything that does not — so the officer is told which medicine, not merely
//  that something is wrong. And there is no Apply button anywhere in here on
//  purpose: a clinical judgement is the officer's to make, and a one-click
//  "correct this" on a model's opinion would be the feature quietly becoming the
//  thing the request said it must not be.
function ClinicalReviewNote({ claimId, source }) {
  const [review, setReview] = React.useState(null);

  React.useEffect(() => {
    if (!claimId) return undefined;
    let alive = true;
    axios.get(claimAiReviewOf, { params: { id: claimId, source } })
      .then(({ data }) => { if (alive && data?.success) setReview(data.review); })
      //  A claim with no review is the normal state, and a failed lookup must not
      //  put an error on a claim that has nothing wrong with it.
      .catch(() => { if (alive) setReview(null); });
    return () => { alive = false; };
  }, [claimId, source]);

  const findings = (review?.findings || []);
  if (!findings.length) return null;

  const when = review?.reviewed_at
    ? new Date(review.reviewed_at).toLocaleDateString('en-GB',
      { day: '2-digit', month: 'short', year: 'numeric' })
    : null;

  return (
    <div style={{
      margin: '0 0 10px', padding: '11px 13px', borderRadius: 10,
      background: '#FFF8EC', border: '1px solid rgba(224,139,47,.38)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 7 }}>
        <Stethoscope size={15} style={{ color: '#8A5510', flexShrink: 0 }} />
        <div style={{ fontSize: 12.5, fontWeight: 800, color: '#8A5510' }}>
          Clinical review — {findings.length} thing{findings.length === 1 ? '' : 's'} to check
        </div>
      </div>

      <div style={{ display: 'grid', gap: 6 }}>
        {findings.map((f, i) => (
          <div key={`${f.code}-${i}`} style={{
            display: 'flex', gap: 8, alignItems: 'flex-start',
            fontSize: 12.2, lineHeight: 1.6, color: BRAND.ink,
          }}>
            <span style={{ color: '#8A5510', flexShrink: 0, fontWeight: 900 }}>·</span>
            <div style={{ minWidth: 0 }}>
              {f.message}
              {/*  WHICH ROW. A warning an officer cannot trace to a line is a
                   warning they cannot act on. */}
              {(f.refs || []).length > 0 && (
                <span style={{ color: BRAND.inkMuted }}>
                  {' ('}{f.refs.map((r) => r.name).filter(Boolean).join(', ')}{')'}
                </span>
              )}
              {/*  A rule and a model are different kinds of claim on the truth,
                   and the officer is entitled to know which they are reading. */}
              {f.from === 'rule' && (
                <span style={{
                  marginLeft: 6, fontSize: 9.5, fontWeight: 800,
                  padding: '1px 5px', borderRadius: 4,
                  background: '#EEF2F7', color: BRAND.inkMuted,
                }}>ARITHMETIC</span>
              )}
            </div>
          </div>
        ))}
      </div>

      <div style={{ marginTop: 8, fontSize: 10.8, color: BRAND.inkMuted, lineHeight: 1.6 }}>
        This is a suggestion for you to weigh, not a rejection — nothing here has
        been changed on the claim{when ? `. Reviewed on ${when}` : ''}.
      </div>
    </div>
  );
}

function Field({ label, value, hint, hintWarn, hintTone, span, edit, onChange, type, options, locked, checkbox }) {
  const editable = edit && !locked;

  //  Green while editing, and only while editing. The label going green plus a
  //  white field is what tells the officer at a glance which boxes are live —
  //  otherwise an edit mode looks identical to a read-only one and the first
  //  keystroke is a guess.
  const box = {
    ...G.field,
    background: editable ? BRAND.surface : '#F4F6FA',
    borderColor: editable ? BRAND.green : BRAND.line,
    color: editable ? BRAND.ink : BRAND.inkMuted,
    cursor: editable ? 'auto' : 'default',
  };

  return (
    <div style={{ minWidth: 0, gridColumn: span ? `span ${span}` : undefined }}>
      <label style={{ ...G.label, color: editable ? BRAND.greenDark : BRAND.inkFaint }}>
        {label}
        {edit && locked && (
          <span style={{ marginLeft: 6, fontWeight: 700, textTransform: 'none',
            letterSpacing: 0, color: BRAND.inkFaint }}>
            (fixed)
          </span>
        )}
      </label>

      {checkbox ? (
        <div style={{ ...box, display: 'flex', alignItems: 'center',
          justifyContent: 'space-between', gap: 8 }}>
          <span>{value ? 'Yes' : 'No'}</span>
          <input type="checkbox" checked={!!value} disabled={!editable}
            onChange={(e) => onChange?.(e.target.checked)}
            aria-label={label} style={{ accentColor: BRAND.green,
              cursor: editable ? 'pointer' : 'default' }} />
        </div>
      ) : options ? (
        <select style={box} value={value == null ? '' : String(value)} disabled={!editable}
          onChange={(e) => onChange?.(e.target.value)} aria-label={label}>
          {/* A blank option only while the current value IS blank — otherwise it
              offers a way to clear a field that should not be cleared by accident. */}
          {(value == null || value === '') && <option value="">—</option>}
          {options.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      ) : (
        <input
          style={box}
          //  TEXT when read-only, whatever `type` says. A date input can only
          //  display YYYY-MM-DD, but read mode passes the dd/mm/yyyy the design
          //  uses — which the browser cannot parse, so it rendered an empty
          //  "dd/mm/yyyy" placeholder and the date looked missing. It was there
          //  all along: the age hint beside it computed correctly from the same
          //  value. Only the editable input needs to be a real date picker.
          type={editable ? (type || 'text') : 'text'}
          value={value == null ? '' : String(value)}
          readOnly={!editable}
          tabIndex={editable ? 0 : -1}
          onChange={(e) => onChange?.(e.target.value)}
          aria-label={label}
        />
      )}

      {/*  `hintWarn` colours the note amber. A field whose value the rules call
           wrong must not explain itself in the same grey as one that is fine —
           the officer reads the hint to decide whether to act on it. */}
      {hint && (
        <div style={{
          fontSize: 10.5, marginTop: 3, lineHeight: 1.45,
          color: BRAND.inkFaint, fontWeight: 400,
          ...(HINT_TONES[hintTone || (hintWarn ? 'warn' : '')] || null),
        }}>{hint}</div>
      )}
    </div>
  );
}

const Fold = React.forwardRef(function Fold(
  { icon: Icon, title, count, children, notReady, wantOpen, openToken, focused,
    // Which of the four this is, for its tone. Falls back to the old single green
    // when absent, so a new Fold added without one still looks deliberate.
    tone: toneKey,
    // The section's real Add handler, where the section has one. Without it the
    // header button falls back to saying the behaviour is not built yet.
    onAdd }, ref,
) {
  const empty = !count;
  const tone = TONE[toneKey] || NEUTRAL_TONE;

  // ── One source of truth for whether this is open ──────────────────────────
  //  `wantOpen` is computed by the parent from two rules:
  //
  //    nothing focused  →  open if the section HAS content. A claim is opened to
  //                        be read, so what it holds should already be on screen.
  //    a section focused → only that one is open. Clicking Diagnosis in Entries
  //                        gives the diagnosis the whole panel instead of leaving
  //                        it competing with three other tables.
  //
  //  It is applied on a TOKEN rather than on every render, so a manual toggle in
  //  between survives: the officer can shut a section without the parent's rule
  //  immediately re-opening it. The token changes when the claim changes or the
  //  focus changes — the two moments the rule should reassert itself.
  const [open, setOpen] = useState(wantOpen);
  useEffect(() => { setOpen(wantOpen); }, [openToken]);   // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div ref={ref} style={{ marginBottom: 9, scrollMarginTop: 8 }}>
      <div style={{
        ...G.fold,
        // A section with nothing in it is visually quieter, so the eye goes to the
        // ones that actually carry the claim.
        //
        // FOCUS is shown by WEIGHT, not by hue: a navy bar used to mark the section
        // you jumped to, which now collides with the Diagnoses tone. A thicker edge
        // plus a ring in the section's own colour says the same thing without
        // fighting the palette — and keeps the tone readable while focused.
        borderLeftColor: empty ? BRAND.line : tone.line,
        borderLeftWidth: focused ? 5 : 3,
        background: empty ? '#F4F6FA' : tone.wash,
        ...(focused ? { boxShadow: `0 0 0 2px ${tone.line}33` } : null),
        ...(open ? { borderRadius: '9px 9px 0 0' } : null),
      }}>
        <button type="button" onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          title={empty ? `No ${title.toLowerCase()} on this claim` : `Show ${title.toLowerCase()}`}
          style={{
            display: 'flex', alignItems: 'center', gap: 9, flex: 1, minWidth: 0,
            border: 0, background: 'none', font: 'inherit', cursor: 'pointer',
            textAlign: 'left', color: BRAND.ink, padding: 0,
          }}>
          <ChevronDown size={14} style={{
            color: BRAND.inkMuted, flexShrink: 0,
            transform: open ? 'none' : 'rotate(-90deg)', transition: 'transform .15s ease',
          }} />
          {/*  The icon carries the tone too. It is the strongest of the three
               signals at a glance — shape AND colour in the same 14px — and it is
               the one that still works when the wash is invisible on a dim screen. */}
          {Icon && <Icon size={14} style={{
            color: empty ? BRAND.inkFaint : tone.ink, flexShrink: 0 }} />}
          <span style={{ fontSize: 12.5, fontWeight: 800,
            color: empty ? BRAND.inkMuted : BRAND.ink }}>
            {title} <span style={{ color: BRAND.inkMuted, fontWeight: 700 }}>({count || 0})</span>
          </span>
          {/* An empty section says so on its bar. Otherwise a closed fold and a
              fold with nothing in it look identical, and the officer opens it to
              find out — which is the click this whole behaviour removes. */}
          {empty && (
            <span style={{ fontSize: 11, color: BRAND.inkFaint, fontWeight: 600 }}>
              none on this claim
            </span>
          )}
        </button>
        {/*  The section's own Add. `onAdd` is the real one where the section has
             been built; the notReady fallback is for the sections that have not.
             Adding a row means the fold has to be OPEN to fill it in, so this
             opens it rather than adding a row into something you cannot see. */}
        <button type="button" style={G.addBtn}
          title={onAdd ? `Add a ${title.replace(/e?s$/, '').toLowerCase()}` : undefined}
          onClick={() => {
            if (!onAdd) { notReady(`Adding a ${title.replace(/e?s$/, '').toLowerCase()}`); return; }
            setOpen(true);
            onAdd();
          }}>
          <Plus size={13} /> Add
        </button>
      </div>
      {open && (
        <div style={{
          border: `1px solid ${BRAND.line}`, borderTop: 0,
          // The left edge continues down the open body, so an expanded section is
          // one banded block rather than a coloured bar with a white slab under it.
          // That band is what you follow when scrolling past three open tables.
          borderLeft: `3px solid ${tone.line}`,
          borderRadius: '0 0 9px 9px', background: tone.wash, overflowX: 'auto',
        }}>
          {/*  A section you can ADD to renders its editor even with nothing in it
               — that is the state you add the first row from, and it carries its
               own empty message. Replacing it with this placeholder is why Add
               appeared to do nothing on a claim with no procedures. */}
          {empty && !onAdd
            ? <div style={{ padding: '16px 14px', fontSize: 12, color: BRAND.inkFaint }}>
                Nothing recorded on this claim.
              </div>
            : children}
        </div>
      )}
    </div>
  );
});

// ── What is WRONG with this claim ───────────────────────────────────────────
//  The banner that did not exist. The rule it carries — an admission cannot be
//  billed on an out-patient tariff — was in this codebase for a long time as a
//  checkbox inside the Advanced Filter dialog and nowhere else, so a claim could
//  be opened, edited, saved and exported without one screen ever mentioning it.
//
//  Placed directly under the identity bar, ABOVE the cards, because it is a
//  statement about the whole claim rather than about any one section. An officer
//  who reads nothing else on this panel reads this.
//
//  ERRORS ARE RED AND WARNINGS ARE AMBER, but neither relies on colour alone: the
//  severity is written out, the icon differs, and the message says what NHIA will
//  do about it. The two are also SORTED, errors first — the thing that gets the
//  claim rejected should not be third in a list of three.
const ISSUE_TONE = {
  error:   { bg: '#FBE9E9', fg: '#A62828', bd: '#F0C4C4', Icon: ShieldAlert,   word: 'Rejected by NHIA' },
  warning: { bg: '#FDF2E3', fg: '#8A5510', bd: '#F2DCB8', Icon: AlertTriangle, word: 'Worth checking' },
};

//  What the tariff catalogue can offer a claim whose own G-DRGs are all
//  out-patient codes. `rescue` comes from claimDetail and is only present on those.
//
//  Three genuinely different answers, and flattening them into one message is what
//  the first version did — it told an officer "nothing can be derived from this
//  claim" on 205 claims, most of which carry a perfectly good ICD-10.
const RescueNote = ({ rescue, busy, onApply }) => {
  if (!rescue) return null;
  const { resolved = [], ambiguous = [], unmatched = [] } = rescue;

  return (
    <>
      {resolved.length > 0 && (
        <div style={{ marginTop: 6, fontSize: 11.5 }}>
          Its ICD-10 settles it:{' '}
          {resolved.map((r) => `${r.icd10 || r.description} → ${r.to.gdrg_code}`).join(', ')}.
          <button type="button" disabled={busy} onClick={() => onApply(null)}
            style={{
              marginLeft: 8, padding: '3px 9px', borderRadius: 6,
              fontSize: 11, fontWeight: 700, cursor: busy ? 'progress' : 'pointer',
              border: '1px solid currentColor', background: '#fff', color: 'inherit',
            }}>
            {busy ? 'Repairing…' : 'Apply'}
          </button>
        </div>
      )}

      {/*  A choice, not a failure. The tariffs differ, so picking one is a billing
           decision — the codes are named here and the officer sets it on the
           diagnosis row below, where the change is visible and attributable.  */}
      {ambiguous.map((a) => (
        <div key={a.row_id} style={{ marginTop: 6, fontSize: 11.5 }}>
          <b>{a.icd10 || a.description}</b> is billable under{' '}
          {a.candidates.length} G-DRGs —{' '}
          {a.candidates.map((c) => `${c.gdrg_code} (${c.tariff})`).join(', ')}.
          They carry different tariffs, so this will not choose for you. Set it on
          the diagnosis row below.
        </div>
      ))}

      {unmatched.length > 0 && (
        <div style={{ marginTop: 6, fontSize: 11.5, opacity: .85 }}>
          {unmatched.map((u) => u.icd10 || u.description).join(', ')}
          {unmatched.length === 1 ? ' is' : ' are'} not in the priced catalogue for
          this scheme, so nothing can be derived from{' '}
          {unmatched.length === 1 ? 'it' : 'them'}.
        </div>
      )}
    </>
  );
};

const IssueBanner = ({ issues, rescue, busy, onApply }) => {
  const list = (issues || []).slice().sort(
    (a, b) => (a.severity === b.severity ? 0 : a.severity === 'error' ? -1 : 1));
  if (!list.length) return null;

  return (
    <div style={{ display: 'grid', gap: 6 }}>
      {list.map((iss, i) => {
        const tone = ISSUE_TONE[iss.severity] || ISSUE_TONE.warning;
        const { Icon } = tone;
        return (
          <div key={`${iss.code}-${i}`} role={iss.severity === 'error' ? 'alert' : 'status'}
            style={{
              display: 'flex', alignItems: 'flex-start', gap: 9,
              padding: '9px 11px', borderRadius: 8, fontSize: 12, lineHeight: 1.5,
              background: tone.bg, color: tone.fg, border: `1px solid ${tone.bd}`,
            }}>
            <Icon size={15} style={{ flexShrink: 0, marginTop: 1 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 800, letterSpacing: '.01em', marginBottom: 1 }}>
                {iss.title}
                <span style={{ fontWeight: 600, opacity: .75, marginLeft: 7, fontSize: 11 }}>
                  {tone.word}
                </span>
              </div>
              <div>{iss.message}</div>

              {/*  A claim carrying nothing but OPD codes cannot be settled from
                   its G-DRGs — but its ICD-10 may still settle it, and RescueNote
                   says which of the three cases this claim is in. Only when the
                   catalogue was not consulted at all does the flat message stand.  */}
              {iss.severity === 'error' && !iss.fixable && (
                rescue
                  ? <RescueNote rescue={rescue} busy={busy} onApply={onApply} />
                  : (
                    <div style={{ marginTop: 4, fontSize: 11.5, opacity: .85 }}>
                      This one cannot be settled from the claim itself — every G-DRG
                      on it is an out-patient code. Choose a service, or record the
                      procedure that was actually done.
                    </div>
                  )
              )}
            </div>

            {iss.fixable && iss.suggested && (
              <button type="button" disabled={busy}
                onClick={() => onApply(iss)}
                title={`Set this claim to ${iss.suggested.service_code}`}
                style={{
                  flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 5,
                  padding: '5px 10px', borderRadius: 6, fontSize: 11.5, fontWeight: 700,
                  cursor: busy ? 'progress' : 'pointer',
                  border: `1px solid ${tone.fg}44`, background: '#fff', color: tone.fg,
                }}>
                <Wrench size={12} />
                {busy ? 'Repairing…' : `Use ${iss.suggested.service_code}`}
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default function ClaimEditor({
  claimId, source, patientName, onNotReady, onClose,
  // Called when a write inside the panel changes the claim — adding a procedure
  // can change the PRINCIPAL, and the row behind this panel shows that service
  // code. Optional: the panel is correct without it, the list would just be stale
  // until reopened.
  onChanged,
  // Called once the claim has loaded and this panel has its real height. The
  // Claims Menu uses it to re-run its scroll: on mount this component is a
  // one-line placeholder, so a scroll attempted then gets clamped and the panel
  // ends up mid-table instead of at the top.
  onReady,
  // Stepping through the month in place. null when there is nowhere to go, so the
  // button can be disabled rather than silently doing nothing at the ends.
  onPrev, onNext, position,
  // How far down the scrollport the identity bar must stick — the height of the
  // table's own sticky column header. Measured by the parent rather than assumed,
  // because that header wraps at narrow widths.
  stickyTop = 0,
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ── Entries as navigation ─────────────────────────────────────────────────
  //  The sidebar list is not decoration: clicking an entry scrolls that section
  //  to the top of whatever is scrolling this panel — the Claims Menu's table
  //  wrapper — so the claim reads as one long form you move around in rather than
  //  a page you hunt through.
  //
  //  `scrollMarginTop` on each section is what keeps a heading from landing
  //  underneath the table's sticky header.
  //  `focus` is which entry was clicked, or null for "just opened — show me what
  //  this claim has". The two header cards are always on screen either way; they
  //  are the claim's identity, not one section among several.
  const [focus, setFocus] = useState(null);
  const refs = {
    bio:           useRef(null),
    service:       useRef(null),
    procedures:    useRef(null),
    diagnoses:     useRef(null),
    investigations: useRef(null),
    medications:   useRef(null),
  };

  //  Clicking an entry gives that section the panel: it opens, the others fold
  //  away, and it scrolls to the top ONLY if it is not already in view. Clicking
  //  the same entry again releases the focus.
  //
  //  The "only if needed" part matters. Focusing Medications folds three sections
  //  away, so the section is usually already near the top of the scrollport — and
  //  scrolling to it anyway produced a lurch on top of the reflow, which made the
  //  medications table feel like it was running away from the cursor.
  const goTo = useCallback((key) => {
    setFocus((cur) => (cur === key ? null : key));
    requestAnimationFrame(() => requestAnimationFrame(() => {
      const el = refs[key]?.current;
      if (!el) return;

      // The scrolling ancestor is the Claims Menu's table wrapper, which this
      // component knows nothing about — so find it rather than assume it.
      let sc = el.parentElement;
      while (sc && sc !== document.body) {
        const oy = getComputedStyle(sc).overflowY;
        if ((oy === 'auto' || oy === 'scroll') && sc.scrollHeight > sc.clientHeight) break;
        sc = sc.parentElement;
      }
      if (!sc || sc === document.body) {
        el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        return;
      }

      const box = sc.getBoundingClientRect();
      const target = el.getBoundingClientRect();
      // Already sitting in the upper part of the viewport? Leave it alone.
      const comfortable = target.top >= box.top
        && target.top <= box.top + box.height * 0.45;
      if (comfortable) return;

      const head = sc.querySelector('thead');
      const headH = head ? head.getBoundingClientRect().height : 0;
      sc.scrollTo({
        top: sc.scrollTop + (target.top - box.top) - headH - 8,
        behavior: 'smooth',
      });
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Each section's desired state, from the single rule described on Fold. The
  // token is what makes the rule reassert itself on a claim change or a focus
  // change, and stay out of the way in between.
  const openToken = `${claimId}:${focus || 'auto'}`;
  const wants = (key, count) => (focus ? focus === key : !!count);

  // Medications is a card rather than a Fold, so it applies the same rule inline.

  // ── Editing the bio ───────────────────────────────────────────────────────
  //  `bio` holds the draft while editing and is null the rest of the time, so
  //  there is no question anywhere in the render about whether a value on screen
  //  came from the database or from the keyboard.
  const [bio, setBio] = useState(null);
  const [saving, setSaving] = useState(false);
  const [schemes, setSchemes] = useState([]);
  const [services, setServices] = useState([]);
  //  Entries ▸ Past History. Kept here rather than inside the entry list so the
  //  dialog survives the list re-rendering as you step between claims.
  const [historyOpen, setHistoryOpen] = useState(false);
  const [docsOpen, setDocsOpen] = useState(false);
  //  What documentation this claim already has, for the marker on the Entries
  //  row. Null until it arrives, and null on failure — a marker that guesses is
  //  worse than no marker, because it is believed.
  const [docStatus, setDocStatus] = useState(null);
  const [serviceBand, setServiceBand] = useState(null);   // 'adult' | 'child' | null
  // Raised by the Procedures fold's Add button; the table appends a row and
  // clears it. A flag rather than a counter because the fold MOUNTS the table on
  // the same click, so there is no earlier value for the table to compare with.
  const [addProcedure, setAddProcedure] = useState(false);
  const [addDiagnosis, setAddDiagnosis] = useState(false);
  const [addInvestigation, setAddInvestigation] = useState(false);
  const [addMedication, setAddMedication] = useState(false);
  const [serviceNote, setServiceNote] = useState(null);   // why the list is empty
  const [serviceSource, setServiceSource] = useState(null);  // the catalogue table
  //  'principal' when the list is the TARIFF BOOK rather than the service
  //  catalogue — a long admission, billed on what was done. Picking one of those
  //  has to write a procedure or diagnosis row as well as the header, so the save
  //  branches on this.
  const [serviceMode, setServiceMode] = useState(null);
  //  'all' | 'procedure' | 'diagnosis' — which half of the tariff book the picker
  //  is showing. On an admission those are two different decisions, and 922 rows
  //  of both together is far less use than 215 of one.
  const [principalKind, setPrincipalKind] = useState('all');

  const editing = bio !== null;
  const setField = (k, v) => setBio((b) => ({ ...b, [k]: v }));

  //  ONE draft covers both cards, and one Edit puts the whole panel into edit
  //  mode. They write to the same row through the same endpoint, so two separate
  //  drafts would let an officer edit the bio, edit the dates, and have the second
  //  save quietly discard the first.
  //
  //  Both cards therefore show Cancel/Save while editing — the same pair, doing
  //  the same thing — so Save is at hand whichever card you happen to be looking
  //  at rather than only on the one you started from.
  const startEdit = () => {
    const h0 = data?.header || {};
    setBio({
      scheme_name:     h0.scheme_name || '',
      member_no:       h0.member_no || '',
      policy_option:   h0.policy_option || '',
      folder_no:       h0.folder_no || '',
      surname:         h0.surname || '',
      other_names:     h0.other_names || '',
      gender:          (h0.gender || '').toLowerCase(),
      date_of_birth:   h0.date_of_birth || '',
      ccc:             h0.ccc || '',
      is_dependant:    !!h0.is_dependant,
      // Discharge only. The visit and admission dates are fixed — the admission IS
      // the visit on essentially every claim, and it decides which month the claim
      // is counted in. Extending a stay means moving the discharge.
      discharge_date:  h0.discharge_date || '',
      service_code:    h0.service_code || '',
      service_name:    h0.service_name || '',
      attendance_type: (h0.attendance_type || '').toLowerCase(),
      // Derived from the service, shown as "From the service above". In the draft
      // because picking a service updates them live.
      specialty_code:  h0.specialty_code || '',
      specialty_name:  h0.specialty_name || '',
      attendance_code: (h0.attendance_code || '').toUpperCase(),
      outcome:         (h0.outcome || '').toLowerCase(),
    });
  };

  //  Picking a service sets three things at once: the code, the name, and — where
  //  the catalogue knows it — whether the claim is inpatient or outpatient. They
  //  are never typed separately, so they cannot end up disagreeing with each other.
  //  ── TAKES THE OPTION, NOT ONLY ITS IDENTITY ─────────────────────────────
  //  SearchSelect hands back both, and it has to: on the tariff book the identity
  //  is `kind|code|name`, because ONE G-DRG covers several conditions — ORTH01A is
  //  Sprain, Strain, Tibial Fracture and Varicose Veins. Looking the option back
  //  up by code alone would select the first of the four whichever was clicked.
  //
  //  A native <select> still calls this with a bare code, and `services.find` is
  //  the fallback for that.
  const pickService = (idOrCode, option) => {
    const opt = option || services.find((o) => o.code === idOrCode);
    const code = opt?.code || idOrCode;
    setBio((b) => ({
      ...b,
      service_code: code,
      //  The chosen entry itself, kept because the save needs its kind, its
      //  ICD-10 and its tariff — none of which survive being reduced to a code.
      principal_pick: opt && opt.kind ? {
        kind: opt.kind, code: opt.code, name: opt.name,
        icd10: opt.icd10 || null,
        tariff: opt.tariff == null ? null : opt.tariff,
        specialty_code: opt.specialty_code || null,
        specialty_name: opt.specialty_name || null,
      } : null,
      service_name: opt?.name || '',
      //  The "From the service above" trio, filled in the way the hospital claims
      //  panel fills it: the specialty the chosen service belongs to. The
      //  catalogue records spe_code / spe_cat, so those are used in preference to
      //  slicing the code — the fallback the panel uses when it has nothing else.
      specialty_code: opt?.specialty || code.slice(0, 4).toUpperCase(),
      specialty_name: opt?.category || opt?.name || '',
      //  The attendance follows the DATES, not the service, so it is left alone
      //  here. Picking an OPD service on a claim discharged three days later must
      //  not quietly turn it back into an outpatient attendance.
    }));
  };

  //  ── The discharge date decides the attendance ────────────────────────────
  //  Same rule the hospital claims panel uses (`dat === d_dat ? Outpatient :
  //  Inpatient`), and the imported claims agree with it without exception. So
  //  moving the discharge to a later day converts an OPD attendance into an
  //  admission here and now, rather than after the save — which also swaps the
  //  service catalogue under the dropdown above.
  const attendanceForDates = (admission, discharge) => {
    const a = String(admission || '').slice(0, 10);
    const d = String(discharge || '').slice(0, 10);
    if (!a || !d) return 'outpatient';
    return d > a ? 'inpatient' : 'outpatient';
  };

  const setDischarge = (v) => setBio((b) => ({
    ...b,
    discharge_date: v,
    attendance_type: attendanceForDates(
      data?.header?.admission_date || data?.header?.visit_date, v),
  }));

  const cancelEdit = () => setBio(null);

  // Which fields actually differ from what is stored. Used to decide whether Save
  // has anything to do, and to name the change in the confirmation.
  const changed = useMemo(() => {
    if (!bio || !data) return [];
    const h0 = data.header || {};
    const same = (a, b) => String(a ?? '') === String(b ?? '');
    return Object.keys(bio).filter((k) => (k === 'is_dependant'
      ? !!bio[k] !== !!h0[k]
      : !same(bio[k], h0[k])));
  }, [bio, data]);

  // The insurance list for the dropdown, from the schemes that actually have
  // claims — not a hardcoded list that drifts from the data.
  useEffect(() => {
    let alive = true;
    //  The list of schemes the facility is contracted to. It does not vary by
    //  claim at all, and this ran on every open.
    cachedGet(insurancetypes, {})
      .then((rows) => {
        if (!alive) return;
        const names = (Array.isArray(rows) ? rows : [])
          .map((r) => r.scheme_name).filter(Boolean);
        setSchemes(names);
      })
      .catch(() => { /* the field falls back to a free-text value */ });
    return () => { alive = false; };
  }, []);

  //  ── The service list, filtered by the patient's age ──────────────────────
  //  Fetched only once editing starts, and keyed on the DRAFT's date of birth so
  //  correcting a wrong birth date re-filters the services in the same sitting —
  //  a claim entered against an adult service for a nine-year-old is exactly the
  //  sort of thing this screen exists to fix.
  //  `data?.header`, not `h` — `h` is declared further down, and a dependency array
  //  is evaluated where it is written, so referencing it here is a temporal-dead-
  //  zone ReferenceError on every render.
  const dobForBand = bio?.date_of_birth ?? data?.header?.date_of_birth;
  const visitForBand = data?.header?.visit_date;
  const ageYears = useMemo(() => {
    if (!dobForBand) return null;
    const d = new Date(dobForBand);
    const on = visitForBand ? new Date(visitForBand) : new Date();
    if (Number.isNaN(d.getTime()) || Number.isNaN(on.getTime())) return null;
    let y = on.getFullYear() - d.getFullYear();
    const m = on.getMonth() - d.getMonth();
    if (m < 0 || (m === 0 && on.getDate() < d.getDate())) y -= 1;
    return y < 0 ? null : y;
  }, [dobForBand, visitForBand]);

  //  Outpatient and inpatient are two SEPARATE catalogues, so the attendance is
  //  part of the request — switching it reloads the list rather than filtering a
  //  list that never held the other kind in the first place.
  //  ── THE DATES DECIDE, NOT THE STORED ATTENDANCE ───────────────────────────
  //  This read `attendance_type`, which is what the claim was SAVED as. So typing
  //  a discharge date equal to the admission date — the way an admission is
  //  corrected to a day case — left the field still requesting the IN-patient
  //  catalogue, and the out-patient services the claim now needs were never
  //  offered. The stay length already followed the draft; the catalogue did not,
  //  and the two disagreed for as long as the card was open.
  //
  //  Attendance follows the DATES. Measured across 2,383 claims earlier in this
  //  project: same day is out-patient, a later discharge is in-patient, with zero
  //  exceptions. So the draft dates are the authority here and the stored value is
  //  only the fallback for a claim that carries no dates at all.
  const draftAdmission = bio?.admission_date ?? data?.header?.admission_date
    ?? data?.header?.visit_date;
  const draftDischarge = bio?.discharge_date ?? data?.header?.discharge_date;
  const settingFromDates = (() => {
    const a = String(draftAdmission || '').slice(0, 10);
    const d = String(draftDischarge || '').slice(0, 10);
    if (!a || !d) return null;
    return d > a ? 'inpatient' : 'outpatient';
  })();
  const settingForList = settingFromDates
    ?? ((bio?.attendance_type ?? data?.header?.attendance_type) === 'inpatient'
      ? 'inpatient' : 'outpatient');

  //  Has the edit in progress turned an admission into a day case? True only
  //  while the card is open and the dates say something different from what the
  //  claim is stored as — which is the moment a service has to be re-picked.
  const flippedToOutpatient = settingFromDates === 'outpatient'
    && (data?.header?.attendance_type === 'inpatient');
  const flippedToInpatient = settingFromDates === 'inpatient'
    && (data?.header?.attendance_type === 'outpatient');

  //  Is the service still the one from BEFORE the flip? Compared against the
  //  reloaded catalogue rather than against a code prefix: the list already holds
  //  exactly the services valid for the new attendance, so "is it in the list" is
  //  the question, and it stays right when the catalogue changes.
  //
  //  Only while the list has actually arrived. An empty `services` means the
  //  request is in flight or the catalogue is unreachable — holding Save on that
  //  would strand the officer on a claim they cannot save and cannot fix.
  const chosenService = String(bio?.service_code ?? data?.header?.service_code ?? '')
    .toUpperCase().trim();

  //  ── IS THE SPECIALTY CODE THE FILE'S, OR THE SERVICE'S? ──────────────────
  //  It decides which of two labels the field carries, and the two say opposite
  //  things about where the value came from.
  //
  //  An IMPORTED claim's specialty comes from the XML's <specialtyAttended>. A claim
  //  raised here has it derived from the service, because pickService writes all
  //  three together. So the question is only "is this imported", and the answer is
  //  the source — not a comparison against the service, which would call the 138
  //  legitimate disagreements a fault.
  const specialtyFromFile = source === 'imported' && !!data?.header?.specialty_code;

  //  ── WHAT THE RULES SAY ABOUT THIS CLAIM'S SPECIALTY ──────────────────────
  //  READ from data.issues rather than recomputed here. claimRules already owns
  //  "the specialty follows the service", and it owns it in two places that must
  //  agree — the JS rule and its SQL twin, which once diverged on 295 claims.
  //  A third copy in the editor would be a third thing to keep in step, and this
  //  field only needs to REPORT the verdict, not reach it.
  const specialtyIssue = (data?.issues || []).find(
    (i) => i.code === 'specialty_not_the_service' || i.code === 'specialty_not_a_group');
  const serviceNeedsChoice = (flippedToOutpatient || flippedToInpatient)
    && services.length > 0
    && !services.some((s) => String(s.code || '').toUpperCase().trim() === chosenService);

  //  How many nights this stay is, so the server can decide whether the same-day
  //  services are on offer at all. Detention and observation are for a stay of
  //  under a day; a two-night admission has to be billed on its procedure or its
  //  diagnosis, and the list comes back empty saying so.
  //
  //  Reads the DRAFT discharge date first — editing the discharge date is exactly
  //  how a one-night stay becomes a three-night one, and the list has to follow
  //  that as it is typed rather than after the save.
  const stayNights = useMemo(() => {
    const adm = data?.header?.admission_date || data?.header?.visit_date;
    const dis = bio?.discharge_date ?? data?.header?.discharge_date;
    if (!adm || !dis) return null;
    const n = Math.round((new Date(dis) - new Date(adm)) / 86400000);
    return Number.isFinite(n) && n >= 0 ? n : null;
  }, [data?.header?.admission_date, data?.header?.visit_date,
    data?.header?.discharge_date, bio?.discharge_date]);

  useEffect(() => {
    if (!editing) return;
    let alive = true;
    //  Keyed on the age band and the attendance, not on the claim — so stepping
    //  between two adult inpatient claims reuses the same answer.
    cachedGet(claimStoreServices,
      {
        age: ageYears, attendance: settingForList, nights: stayNights,
        //  ── ASK FOR THE TARIFF BOOK ON A LONG ADMISSION ──────────────────────
        //  An admission of more than one night has no same-day service to bill —
        //  detention is for a stay of under a day — so the Service dropdown was
        //  EMPTY with a note telling the officer to go elsewhere. It now offers the
        //  two tariff books: pick the procedure performed or the diagnosis treated,
        //  and that becomes the principal G-DRG.
        //
        //  `principal=1` is a GATE the server holds shut by default, and this is
        //  the promise that opens it: this screen writes the chosen row into the
        //  claim's own procedures or diagnoses, not just the header. A caller that
        //  only set the header would put a procedure's code onto an existing
        //  diagnosis and leave its ICD-10 mismatched.
        principal: 1,
      })
      .then((res) => {
        if (!alive) return;
        setServices(res?.options || []);
        setServiceBand(res?.band || null);
        //  'principal' means picking one writes a row as well. Kept so the save
        //  knows which endpoint it owes.
        setServiceMode(res?.mode || null);
        // Why the list is empty, when it is. Without this the field just looks
        // broken rather than saying the catalogue has not been loaded yet.
        setServiceNote(res?.note || null);
        setServiceSource(res?.source || null);
      })
      .catch(() => {
        if (!alive) return;
        setServices([]);
        setServiceNote('The service list could not be loaded.');
      });
    return () => { alive = false; };
    //  `stayNights` is a dependency, so extending the discharge date past one
    //  night re-fetches and the day-case services disappear while you are still
    //  looking at the form.
  }, [editing, ageYears, settingForList, stayNights]);

  const saveBio = async () => {
    if (!changed.length) { cancelEdit(); return; }

    // Ask the server what this change implies before committing to it. Two of the
    // editable fields reach beyond their own column — the visit date moves which
    // month the claim counts in, and a typed-in claim's folder number is what its
    // medications are matched on — and the officer should hear that in the
    // confirmation, not discover it after.
    let warnings = [];
    try {
      const { data: pre } = await axios.post(claimStoreBioPreview, bio,
        { params: { id: claimId, source } });
      warnings = pre?.warnings || [];
    } catch { /* the confirmation still stands without them */ }

    //  What is actually being saved, named field by field, with the server's
    //  warnings underneath in their own tone. The native dialog could only show
    //  one blob of text, so "Save 4 changes to this claim?" was the whole of it —
    //  four unnamed changes, and any warning indistinguishable from the question.
    const ok = await confirmClaim({
      title: `Save ${changed.length} change${changed.length === 1 ? '' : 's'}?`,
      lead: 'These fields will be written to the claim:',
      items: [
        ...changed.map((k) => ({ text: describeChange(k, data?.header, bio) })),
        ...warnings.map((w) => ({ text: w, warn: true })),
      ],
      confirmText: 'Save changes',
    });
    if (!ok) return;

    setSaving(true);
    try {
      //  ── A PRINCIPAL IS NOT A SERVICE, AND IS SAVED DIFFERENTLY ────────────
      //
      //  On a long admission the Service list is the TARIFF BOOK, and the chosen
      //  entry has to become a row in the claim's own procedures or diagnoses —
      //  "then it will move to be part of the procedures or a diagnosis at the
      //  bottom". Sent through the bio save instead, `service_code` would be
      //  stamped onto the claim's dearest EXISTING diagnosis, giving a real
      //  diagnosis a procedure's G-DRG and leaving its ICD-10 mismatched.
      //
      //  So the row is written first, through the endpoint that owns both halves.
      //  The rest of the bio — the dates, the outcome, the attendance — then saves
      //  normally, minus the service fields that endpoint has already settled.
      //  Taken from what was PICKED, not looked back up by code: one G-DRG covers
      //  several conditions, so `find(o => o.code === …)` would resolve ORTH01A to
      //  "Sprain" whichever of its four the officer actually chose.
      const picked = serviceMode === 'principal' ? bio.principal_pick : null;

      if (picked) {
        const { data: pr } = await axios.post(claimStorePrincipal, picked,
          { params: { id: claimId, source } });

        if (!pr?.success) throw new Error(pr?.message || 'The principal could not be set.');

        //  What else the officer changed on this card, without the three fields
        //  the principal just decided. An empty patch is not sent at all —
        //  updateBio refuses one, and that would read as the save failing.
        const rest = { ...bio };
        delete rest.service_code; delete rest.service_name;
        delete rest.specialty_code; delete rest.specialty_name;
        //  A DRAFT FIELD, NOT A COLUMN. `principal_pick` exists so the save knows
        //  what was chosen; updateBio whitelists what it recognises, so this would
        //  be dropped rather than break — but sending a nested object to a bio
        //  save is the kind of thing that becomes a real bug the day the whitelist
        //  gains a loop.
        delete rest.principal_pick;
        const stillChanged = changed.filter((k) => !(
          k === 'service_code' || k === 'service_name'
          || k === 'specialty_code' || k === 'specialty_name'));

        let after = pr;
        if (stillChanged.length) {
          const { data: res2 } = await axios.put(claimStoreBio, rest, {
            params: { id: claimId, source },
          });
          if (res2?.success) after = res2;
        }
        setData(after);
        setBio(null);
        if (onChanged) onChanged(after);
        notifyClaim({
          title: `${picked.code} is now the principal`,
          lead: `Added to this claim as a ${picked.kind}, and the specialty set from `
              + 'the tariff book:',
          items: [
            { text: `${picked.kind === 'procedure' ? 'Procedure' : 'Diagnosis'}: `
                  + `${picked.code} — ${picked.name}` },
            ...(picked.specialty_code
              ? [{ text: `Specialty: ${picked.specialty_code}`
                       + (picked.specialty_name ? ` — ${picked.specialty_name}` : '') }]
              : []),
            ...(picked.tariff == null
              ? [{ text: 'The tariff book carries no price for this entry, so the '
                       + 'claim shows none.', warn: true }]
              : [{ text: `Tariff: GHS ${Number(picked.tariff).toFixed(2)}` }]),
          ],
        });
        setSaving(false);
        return;
      }

      const { data: res } = await axios.put(claimStoreBio, bio, {
        params: { id: claimId, source },
      });
      if (res?.success) {
        // Adopt the RE-READ claim the server returned rather than the draft, so
        // anything the database normalised or rejected is what ends up on screen.
        setData(res);
        setBio(null);
        //  Tell the LIST as well. The row behind this panel carries the claim's
        //  issue codes, and the bio card is where the two faults that produce
        //  them most often get corrected — the patient name and the date of
        //  birth. Without this the officer fixes the name, the editor agrees the
        //  claim is clean, and the red REJECT badge on the row stays put until
        //  the whole month is reloaded.
        if (onChanged) onChanged(res);
        // Child rows renamed alongside a flat claim's folder number. Reported
        // rather than left as a silent side effect.
        const moved = Object.entries(res.cascade || {})
          .filter(([, n]) => n > 0)
          .map(([t, n]) => `${n} in ${t}`);
        if (moved.length) {
          notifyClaim({
            title: 'Folder number changed',
            lead: 'A typed-in claim matches its rows on the folder number, so these '
                + 'moved with it:',
            items: moved,
          });
        }
      } else {
        setError(res?.message || 'Could not save');
      }
    } catch (err) {
      setError(err?.response?.data?.message || err.message);
    } finally {
      setSaving(false);
    }
  };

  //  Applying a repair from the banner. ONE claim, and dryRun is explicitly false
  //  because the officer has read the message and clicked the button naming the
  //  code — the preview step the bulk repair needs would just be the banner again.
  //
  //  The response carries the re-read claim's remaining issues, so the banner
  //  corrects itself: a claim that had two problems and had one fixed still shows
  //  the other rather than going quiet and looking done.
  const [repairing, setRepairing] = useState(false);
  //  `issue` is null when the button came from RescueNote — there the correction
  //  is not one of the claim's own codes but the tariff catalogue's answer for its
  //  ICD-10, so the confirmation is worded from the rescue instead. Both go
  //  through the same endpoint; the server re-derives either way and this never
  //  sends it a code to write.
  const applyRepair = useCallback(async (issue) => {
    const resolved = data?.rescue?.resolved || [];

    //  Two different repairs, so two different questions. Each lists the exact
    //  rows it will rewrite: a bare "\n" separated list in a native dialog gave
    //  no way to tell the heading from the lines under it.
    const ok = issue?.suggested
      ? await confirmClaim({
          title: `Bill this claim on ${issue.suggested.service_code}?`,
          lead: `Taken from the claim's own ${issue.suggested.from}.`,
          items: [
            { text: `Service becomes ${issue.suggested.service_code}` },
            { text: `Specialty becomes ${issue.suggested.specialty_code}` },
          ],
          confirmText: 'Apply repair',
        })
      : await confirmClaim({
          title: `Re-code ${resolved.length} diagnosis line${resolved.length === 1 ? '' : 's'}?`,
          lead: 'Taken from the tariff catalogue for this scheme:',
          items: resolved.map((r) => ({
            text: `${r.icd10 || r.description}:  ${r.was} → ${r.to.gdrg_code} (${r.to.tariff})`,
          })),
          note: 'The ICD-10 does not change. Only the billing code on it does.',
          confirmText: 'Re-code',
        });
    if (!ok) return;

    setRepairing(true);
    try {
      const { data: res } = await axios.post(claimStoreRepair,
        { ids: [Number(claimId)], dryRun: false });
      const mine = (res?.results || [])[0];
      if (mine?.status === 'failed') {
        setError(mine.reason || 'The repair did not go through.');
      } else if (mine?.status === 'unfixable' || mine?.status === 'ambiguous') {
        setError(mine.reason);
      }
      //  Re-read rather than patching state from the response: what appears has
      //  to be what the database now holds, the same rule the bio save follows.
      //  The re-read claim goes up to the list too, so the row's REJECT badge
      //  clears on the same click that repaired it.
      const fresh = await load();
      if (onChanged) onChanged(fresh);
    } catch (err) {
      setError(err?.response?.data?.message || err.message);
    } finally {
      setRepairing(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [claimId, onChanged, data]);

  const notReady = useCallback((what) => {
    if (onNotReady) onNotReady(what);
  }, [onNotReady]);

  //  Has this claim ever finished loading? A REF, not state: it must not
  //  itself trigger a render, and `load` must not take it as a dependency.
  //  Stores WHICH claim is on screen, not merely that something is. Prev/Next
  //  changes claimId, and a bare boolean would suppress the loading state for
  //  the new claim too — leaving the previous patient's figures on screen
  //  while the next one fetched. On a claims run that is a misread waiting to
  //  happen, so a different claim always blanks first.
  const hasLoadedRef = useRef(null);

  //  ── Why a refresh must not raise `loading` ────────────────────────────────
  //  Every save calls back into here to pull the recalculated totals. Setting
  //  `loading` swaps the entire form for the "Loading the full claim…" block,
  //  which UNMOUNTS it — so the scroll container collapses to nothing, the
  //  browser clamps scrollTop to 0, and the officer is thrown back to the top
  //  of the claim. Saving one medication at the bottom of a long claim meant
  //  scrolling all the way down again for the next one.
  //
  //  The blanking screen is only right the FIRST time, when there is nothing
  //  on screen to preserve. After that the old figures stay visible for the
  //  few hundred milliseconds the fetch takes and are replaced in place, which
  //  is both less disruptive and closer to the truth — the claim is still
  //  there, it is only being re-read.
  //  RETURNS the claim it loaded, so a caller that re-read after writing can hand
  //  the fresh claim — and therefore its recomputed `issues` — up to the list
  //  without a second request.
  const load = useCallback(async () => {
    const claimKey = `${source}:${claimId}`;
    if (hasLoadedRef.current !== claimKey) setLoading(true);
    setError(null);
    try {
      const { data: d } = await axios.get(claimStoreDetail, { params: { id: claimId, source } });
      if (d?.success) { setData(d); hasLoadedRef.current = claimKey; return d; }
      setError(d?.message || 'Could not load this claim'); setData(null);
      return null;
    } catch (err) {
      setError(err?.response?.data?.message || err.message);
      setData(null);
      return null;
    } finally { setLoading(false); }
  }, [claimId, source]);

  useEffect(() => { load(); }, [load]);

  // ══ THE SPECIALTY, SETTLED ON OPEN ════════════════════════════════════════
  //
  //  "If the system is able to determine which one is wrong, I think it usually
  //   automatically fix it... So on opening the claim, automatically, such issues
  //   can be just fixed. So there is a progress with fix, and then it fix it, and
  //   then that's it."
  //
  //  The claim showing "Should be MEDI — MEDI31A belongs to MEDI. Use Repair to
  //  correct it." was being told to press a button that CANNOT ACT ON IT: the
  //  repair endpoint reads `source: 'imported'` and takes integer ids, and a
  //  hospital claim is neither. So the officer was reading an instruction, following
  //  it, and getting nothing — on a fault the system had already worked out the
  //  answer to.
  //
  //  ── WHY THIS IS A SEPARATE REQUEST AND NOT PART OF THE GET ────────────────
  //  Because a read must not write. claimDetail is called by the claims list, the
  //  export, the multi-vet sweep and the error report; a write folded into it would
  //  rewrite a whole month the moment somebody opened the table.
  //
  //  ── ONCE PER CLAIM, AND THE REF IS SET BEFORE THE AWAIT ───────────────────
  //  `data` is a dependency, and the re-read below REPLACES data — so without the
  //  guard this effect would call itself. Keyed on `source:claimId` rather than a
  //  boolean, because Prev/Next swaps the claim under a panel that never unmounts
  //  and the next claim does need checking.
  const settledRef = useRef(null);
  const [settling, setSettling] = useState(false);
  const [settled, setSettled] = useState(null);

  //  A new claim starts with no note. Without this the previous patient's
  //  "Corrected automatically" line would sit under the next one's specialty.
  useEffect(() => { setSettled(null); setSettling(false); }, [source, claimId]);

  useEffect(() => {
    if (loading || error || !data) return undefined;
    //  ── BOTH MODELS. THIS GUARD IS GONE, AND IT WAS THE WHOLE PROBLEM ─────
    //
    //  It read `if (source !== 'facility') return undefined;` — so on an
    //  IMPORTED claim the auto-fix was never even requested. The claim the
    //  facility sent three times running is imported (folder E-0029-26033380,
    //  MEDI28A billed under an OBGY specialty), so every time they reopened it
    //  the correction they had been told was built did not run.
    //
    //  Overruled: "add it to the auto fix... fix it once and for all."
    const claimKey = `${source}:${claimId}`;
    if (settledRef.current === claimKey) return undefined;

    //  Is there anything to settle? Asked from the claim's OWN issues and its own
    //  blank field — never re-derived here. claimRules owns "the specialty follows
    //  the service"; this only decides whether to ask the server to apply it.
    const fault = (data.issues || []).find(
      (i) => (i.code === 'specialty_not_the_service' || i.code === 'specialty_not_a_group')
        && i.suggested && i.suggested.to);
    const noDescription = !String(data.header?.specialty_name || '').trim();
    if (!fault && !noDescription) { settledRef.current = claimKey; return undefined; }

    settledRef.current = claimKey;
    let alive = true;
    setSettling(true);
    setSettled(null);

    axios.post(claimStoreSettleSpecialty, { id: claimId, source })
      .then(async ({ data: res }) => {
        if (!alive) return;
        if (!res?.applied) return;
        setSettled(res.changes || []);
        //  Re-read rather than patching state from the response — what appears has
        //  to be what the database now holds, the same rule the bio save and the
        //  repair both follow. The fresh claim goes up to the list too, so the
        //  row's badge clears without a reload.
        const fresh = await load();
        if (alive && onChanged) onChanged(fresh);
      })
      //  SILENT on failure, deliberately. The issue banner and the field hint
      //  already say what is wrong with the claim, and the officer did not ask for
      //  this — putting an error in front of them about a fix they never requested
      //  would be noise about a claim they can still repair by hand.
      .catch(() => {})
      .finally(() => { if (alive) setSettling(false); });

    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, loading, error, source, claimId]);

  //  What to say under the two fields. Split by FIELD, not one banner: the officer
  //  circled these two boxes, and a note beside the value it explains is read where
  //  a banner at the top of a long claim is not.
  const settledFor = (field) => (settled || []).find((c) => c.field === field);

  //  ── The Documentation marker ──────────────────────────────────────────────
  //  Fetched alongside the claim rather than folded into claimDetail: it reads
  //  three tables that have nothing to do with the claim form, and a slow vitals
  //  lookup should not hold up the panel the officer is waiting for.
  //
  //  `docsOpen` is a dependency on purpose. Closing the dialog re-reads it, so
  //  notes just typed or results just saved are reflected without a page reload —
  //  which is exactly when the officer looks back at the row.
  useEffect(() => {
    if (!claimId) { setDocStatus(null); return undefined; }
    let alive = true;
    axios.get(claimStoreDocStatus, { params: { id: claimId, source } })
      .then(({ data }) => { if (alive && data?.success) setDocStatus(data); })
      //  Silent. This is a convenience marker on a row; a failed count is a
      //  reason to show nothing, not to put an error in front of the officer.
      .catch(() => { if (alive) setDocStatus(null); });
    return () => { alive = false; };
  }, [claimId, source, docsOpen]);

  //  ── Announcing the panel is ready, ONCE PER CLAIM ─────────────────────────
  //  The parent's onReady scrolls this panel to the top of the table. That is
  //  right exactly once — when the claim is opened and the placeholder has been
  //  replaced by a page of cards, so the earlier scroll attempt was clamped short.
  //
  //  IT WAS FIRING ON EVERY SAVE. The effect depended on `data`, and every write
  //  in this panel calls load() to pull the recalculated totals, which sets `data`
  //  to a NEW OBJECT. New identity, effect re-runs, onReady fires, the parent
  //  scrolls the claim back to the top. So adding a medication at the bottom of a
  //  446-line claim threw the officer back up to Patient Bio, and the next one had
  //  to be scrolled down to all over again — for every medication, diagnosis,
  //  procedure and investigation added.
  //
  //  Not fixable by making `load` avoid setState — the recalculated totals are the
  //  whole reason it re-reads. What was wrong is treating "data arrived" as "the
  //  panel just opened". Those coincide the first time and never again.
  //
  //  Keyed on source:claimId, not a bare boolean: Prev/Next swaps the claim under
  //  a panel that never unmounts, and a NEW claim does want placing at the top.
  const announcedRef = useRef(null);
  useEffect(() => {
    if (loading || error || !data || !onReady) return undefined;
    const claimKey = `${source}:${claimId}`;
    if (announcedRef.current === claimKey) return undefined;
    announcedRef.current = claimKey;
    //  Two frames: one for React to commit the DOM, one for layout to settle, so
    //  the height the parent measures is the final one.
    let b = 0;
    const a = requestAnimationFrame(() => { b = requestAnimationFrame(() => onReady()); });
    //  BOTH frames cancelled. The inner id was previously `return`ed out of the
    //  rAF callback, where nothing reads it, so closing the panel between the two
    //  frames still fired onReady into a dead parent.
    return () => { cancelAnimationFrame(a); if (b) cancelAnimationFrame(b); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, error, data, source, claimId]);

  const h = data?.header || {};
  const t = data?.totals || {};
  const meds = data?.medications || [];

  // ── The order a claim is actually worked in ─────────────────────────────────
  //  Past History first: it is what you read BEFORE the patient's own details,
  //  because it is the context the rest is judged against.
  //
  //  Then one entry for Patient Bio AND Service Info. They were two, which implied
  //  two places to go — but they are one row of the panel, side by side, and they
  //  share a single `editing` state: the Edit button on either opens both. Two
  //  entries pointing at one thing is two chances to think you missed something.
  //
  //  Then the four data sections in the order the claim is built — procedure,
  //  diagnosis, investigation — and Medications last, because it is the longest and
  //  the one you scroll to rather than tab through.
  const entries = useMemo(() => ([
    { key: 'history',        icon: History,      label: 'Past History',  count: null },
    { key: 'bio',            icon: Users,        label: 'Patient Bio / Service Info', count: null, action: 'edit' },
    { key: 'procedures',     icon: Stethoscope,  label: 'Procedure',     count: data?.procedures?.length || 0, action: 'add' },
    { key: 'diagnoses',      icon: FileText,     label: 'Diagnosis',     count: data?.diagnoses?.length || 0, action: 'add' },
    { key: 'investigations', icon: FlaskConical, label: 'Investigation', count: data?.investigations?.length || 0, action: 'add' },
    { key: 'medications',    icon: Pill,         label: 'Medications',   count: meds.length },
    //  ── Documentation ────────────────────────────────────────────────────────
    //  LAST, and deliberately: it is what a claim is supported BY rather than
    //  what it is built from. The five above are the billable rows; this is the
    //  paperwork behind them, and it is read when a claim is queried rather than
    //  while it is being assembled.
    //
    //  The section itself is not built yet — no `refs.documentation`, so the
    //  entry says so when clicked rather than navigating nowhere. That is the
    //  same treatment Past History had until it was wired, and it is the honest
    //  state to ship: the menu now shows where documentation WILL live, which is
    //  what makes the shape of the panel reviewable before the content exists.
    { key: 'documentation',  icon: ClipboardList, label: 'Documentation', count: null },
  ]), [data, meds.length]);

  //  ── Loading, WITHOUT tearing the panel down ───────────────────────────────
  //
  //  This used to return one line of text for `loading`, which threw away the
  //  whole editor — header bar, Prev/Next, both cards. Stepping through claims on
  //  a remote server therefore looked like the panel had closed and reopened
  //  empty, and on a slow link there was a second or two where nothing on screen
  //  said the system was doing anything at all.
  //
  //  Two different situations, and they want different things:
  //
  //    FIRST open   there is no claim to keep, so a SKELETON of the real layout
  //                 holds the shape and the height rather than collapsing to a
  //                 sentence and then jumping to full size.
  //    Prev/Next    the previous claim is still in `data` — the fetch does not
  //                 clear it — so it stays on screen, dimmed, under a spinner.
  //                 Nothing moves, the height does not change, and it is obvious
  //                 which direction you asked for.
  if (loading && !data) {
    const bar = { background: '#E7EDF6', borderRadius: 6 };
    return (
      <div style={{ padding: '14px 16px', background: BRAND.canvas }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 9,
          padding: '10px 14px', borderRadius: 10, marginBottom: 12,
          background: BRAND.navy, color: '#fff',
        }}>
          <Loader2 size={15} className="mf-spin" />
          <span style={{ fontSize: 13, fontWeight: 700 }}>Loading claim…</span>
        </div>
        <div style={{
          display: 'grid', gap: 12,
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        }}>
          {[0, 1].map((i) => (
            <div key={i} style={{
              border: `1px solid ${BRAND.line}`, borderRadius: 12,
              background: '#fff', padding: 14,
            }}>
              <div style={{ ...bar, width: '42%', height: 13, marginBottom: 14 }} />
              <div style={{
                display: 'grid', gap: 10,
                gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
              }}>
                {Array.from({ length: 9 }).map((_, k) => (
                  <div key={k}>
                    <div style={{ ...bar, width: '64%', height: 8, marginBottom: 6 }} />
                    <div style={{ ...bar, height: 28 }} />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        <style>{'@keyframes mfspin{to{transform:rotate(360deg)}}'
          + '.mf-spin{animation:mfspin .9s linear infinite}'}</style>
      </div>
    );
  }
  if (error) {
    return (
      <div style={{ padding: '20px 16px', fontSize: 12.5, color: BRAND.danger }}>
        {error}
        <button type="button" onClick={load}
          style={{ ...G.editBtn, marginLeft: 10 }}>Try again</button>
      </div>
    );
  }

  return (
    <div style={{
      display: 'grid', gap: 12, alignItems: 'start', padding: '14px 16px',
      background: BRAND.canvas,
      //  The overlay below is positioned against this box.
      position: 'relative',
      // Sidebar drops beneath the main column on anything narrow rather than
      // squeezing a 14-column claim into half the width.
      gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 300px)',
      // ── Why a min-height ──────────────────────────────────────────────────
      //  Focusing a section folds the other three away, which used to cut the
      //  panel's height by hundreds of pixels. The scroll container's scrollHeight
      //  shrank with it, the browser clamped scrollTop to fit, and the whole view
      //  lurched — so clicking Medications appeared to drag the page around and
      //  the table was awkward to work with.
      //
      //  Holding a screenful means folding changes what is IN the panel without
      //  changing how tall it is, so nothing moves that the officer did not move.
      //  It also guarantees the sticky sidebar has a tall enough row to stick in.
      //  alignContent is left at its default (stretch) on purpose: it makes the
      //  single grid row fill that min-height, which is what gives the sticky
      //  sidebar a tall enough containing block to stick in. `alignContent:
      //  'start'` would pack the row to its content height and the nav would
      //  scroll away again the moment the main column got short.
      minHeight: 'calc(100vh - 250px)',
    }}>
      {/*  ── Stepping to another claim ────────────────────────────────────────
           The claim you were on stays where it is, dimmed, with a spinner over
           it. Nothing collapses, the panel keeps its height, and the wait is
           visibly the system working rather than the editor having closed.

           `pointerEvents` blocks clicks on the old claim while the new one is in
           flight — editing a row that is about to be replaced writes to the
           claim you have already left. */}
      {loading && (
        <div style={{
          position: 'absolute', inset: 0, zIndex: 40,
          background: 'rgba(244,247,251,.72)',
          display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
          paddingTop: 90, pointerEvents: 'all',
        }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 9,
            padding: '10px 16px', borderRadius: 999,
            background: BRAND.navy, color: '#fff',
            boxShadow: '0 8px 24px rgba(15,31,61,.28)',
            position: 'sticky', top: 90,
          }}>
            <Loader2 size={15} className="mf-spin" />
            <span style={{ fontSize: 12.5, fontWeight: 700 }}>Loading claim…</span>
          </div>
          <style>{'@keyframes mfspin{to{transform:rotate(360deg)}}'
            + '.mf-spin{animation:mfspin .9s linear infinite}'}</style>
        </div>
      )}

      {/* ══ MAIN ══════════════════════════════════════════════════════════ */}
      <div style={{ display: 'grid', gap: 12, minWidth: 0, opacity: loading ? .45 : 1 }}>

        {/* ── Identity bar, STICKY ──────────────────────────────────────────
            Which claim this is, and the controls for moving off it. The row it
            belongs to has scrolled out of sight above, so the panel has to say
            for itself whose claim is open.

            It sticks because Prev/Next are needed from anywhere in a page-tall
            form — having to scroll back to the top to reach Next made stepping
            through a month two scrolls per claim instead of one click.

            `top` is the measured height of the table's own sticky column header,
            so the two stack rather than overlap. z-index sits above the cards but
            below the table header, which owns the very top of the scrollport. */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
          padding: '7px 12px', borderRadius: 8,
          background: BRAND.navy, color: '#fff', fontSize: 12,
          position: 'sticky', top: stickyTop, zIndex: 2,
          // The cards scroll underneath, so the bar needs a visible edge or the
          // two read as one block.
          boxShadow: '0 4px 12px rgba(15,31,61,.22)',
        }}>
          <SquarePen size={13} />
          <strong style={{ fontSize: 13 }}>
            {(patientName || `${h.surname || ''} ${h.other_names || ''}`).trim() || 'Claim'}
          </strong>
          <span style={{ opacity: .75 }}>
            folder {h.folder_no || '—'} · {h.member_no || '—'}
            {h.ccc ? ` · CCC ${h.ccc}` : ''} · {h.visit_date || ''}
          </span>

          {/* Step through the month in place. A claims officer works a month in
              order; closing the panel, hunting for the next row and clicking its
              pencil is three actions for what should be one. */}
          <span style={{
            marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6,
          }}>
            {position?.total > 1 && (
              <span style={{
                fontSize: 11, opacity: .8, fontVariantNumeric: 'tabular-nums',
                whiteSpace: 'nowrap', marginRight: 2,
              }}>
                {position.index.toLocaleString()} of {position.total.toLocaleString()}
              </span>
            )}
            {/*  Both are dead while a claim is in flight. Clicking Next three
                 times on a slow link fired three fetches whose responses landed
                 in whatever order they finished, so the editor could settle on a
                 claim two back from the one asked for. */}
            <button type="button" onClick={onPrev || undefined}
              disabled={!onPrev || loading}
              title={loading ? 'Loading…'
                : onPrev ? 'Previous claim' : 'This is the first claim'}
              aria-label="Previous claim"
              aria-busy={loading || undefined}
              style={barBtn(!onPrev || loading)}>
              {loading ? <Loader2 size={14} className="mf-spin" /> : <ChevronLeft size={14} />} Prev
            </button>
            <button type="button" onClick={onNext || undefined}
              disabled={!onNext || loading}
              title={loading ? 'Loading…'
                : onNext ? 'Save nothing yet — moves to the next claim' : 'This is the last claim'}
              aria-label="Next claim"
              aria-busy={loading || undefined}
              style={{ ...barBtn(!onNext || loading),
                background: onNext && !loading ? BRAND.green : 'transparent',
                borderColor: onNext && !loading ? BRAND.green : 'rgba(255,255,255,.25)' }}>
              Next {loading ? <Loader2 size={14} className="mf-spin" /> : <ChevronRight size={14} />}
            </button>
            {onClose && (
              <button type="button" onClick={onClose} style={barBtn(false)}>
                Close
              </button>
            )}
          </span>
        </div>


        {/*  Whether this claim will survive NHIA. Above the cards on purpose: it
             is a statement about the whole claim, not about one section of it,
             and it is the first thing an officer needs to know on opening one.
             Renders nothing at all when the claim is clean — a permanent "no
             problems" strip is noise that trains people to skip this space.  */}
        <IssueBanner issues={data?.issues} rescue={data?.rescue}
          busy={repairing} onApply={applyRepair} />

        {/*  ── WHAT THE CLINICAL REVIEW SAID ──────────────────────────────────
             Directly under the rule banner and deliberately NOT inside it. The
             banner above states facts about the claim — NHIA will reject this,
             this field is empty. This states an opinion: the treatment does not
             appear to fit the condition billed.

             "This one should be in a form of a warning, because we cannot force
              the people to say it is an error." So it is amber, it is separate,
             it names what it is looking at, and it changes nothing. */}
        <ClinicalReviewNote claimId={claimId} source={source} />

        {/*  ── PUT A WHOLE TEMPLATE ON THIS CLAIM ────────────────────────────
             Below the banner and above the sections, because that is the order of
             the decision: what is wrong with this claim, then what it should look
             like, then the individual rows.

             It sets all four sections at once, and in 'replace' mode it DELETES
             what is there — so it is folded away behind one button, and the panel
             asks the server what would happen before the button goes live. The
             server judges this claim against this patient's own date of birth,
             gender and attendance, not against the template's label.

             BOTH MODELS, as of 2026-09-09. This read `source === 'imported'`
             and the note here said a write on a typed-in claim "has nowhere to
             land" — which had stopped being true. saveDiagnosis, saveProcedure,
             saveMedication and saveInvestigation each branch on `detail.source`
             and each has a flat path into doctors_analysis and the ward tables,
             and these four panels are `readOnly={false}` on both sources. The
             writers were ready; the apply endpoint was passing them the literal
             'imported', so a typed-in claim came back "No such claim".

             Worse where the two models share a number: hospital claim 540 and
             imported claim 540 are different patients, and the endpoint would
             have rewritten the wrong one's rows. The claim's model now travels
             with its id — see readIds in routes/claimTemplateApply.js.

             `notReady` is NOT the test: it is the callback that reports an
             unbuilt feature, so it is always truthy and `!notReady` would have
             hidden this panel permanently. */}
        {data && (
          <div style={{ marginTop: 8 }}>
            <ClaimTemplateApply
              claimId={claimId}
              //  Which model, so the endpoint writes to the right tables and
              //  cannot confuse this claim with the other model's claim 540.
              source={data.source}
              //  A re-read, not a patch. Applying a template can change the
              //  resolved principal and therefore the claim's billed service, and
              //  the row behind this panel shows that code — so the whole claim is
              //  loaded again and the parent told, exactly as a single-row save does.
              onApplied={async () => {
                const fresh = await load();
                if (fresh && onChanged) onChanged(fresh);
              }} />
          </div>
        )}

        {/*  ── AND WHAT NHIA ACTUALLY SAID ───────────────────────────────────
             ABOVE the IssueBanner's siblings and below the banner itself, in that
             order deliberately: the banner is what WE think of the claim, this is
             what the scheme DID with it. A verdict outranks a prediction — an
             officer looking at a claim NHIA has already rejected does not need our
             opinion of it first.

             Renders nothing when there is no verdict, because an empty panel
             would read as approval and a claim with no verdict is just as likely
             never to have been submitted. See ClaimVerdictPanel. */}
        <ClaimVerdictPanel claimId={claimId} />

        {/* Member details + Service & dates */}
        <div style={{
          display: 'grid', gap: 12, minWidth: 0,
          gridTemplateColumns: 'repeat(auto-fit, minmax(330px, 1fr))',
        }}>
          <section ref={refs.bio} style={{
            ...G.card, scrollMarginTop: 8,
            borderLeftWidth: editing ? 4 : 3,
            boxShadow: editing ? `0 0 0 1px ${BRAND.green}` : 'none',
          }}>
            <header style={{
              ...G.cardHead,
              background: editing ? BRAND.greenSoft : undefined,
            }}>
              <span style={{
                ...G.cardIcon,
                background: editing ? '#D8EEE0' : BRAND.navySoft,
                color: editing ? BRAND.greenDark : BRAND.navy,
              }}><Users size={15} /></span>
              <span style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}>
                  <span style={G.cardTitle}>Member Details</span>
                  {/* Only while something has actually changed. A permanent
                      "Unsaved" chip would cry wolf the moment you clicked Edit. */}
                  {editing && changed.length > 0 && (
                    <span style={{
                      padding: '1px 8px', borderRadius: 20, fontSize: 9.5, fontWeight: 800,
                      letterSpacing: '.04em', background: '#F6E4C4', color: '#8A5510',
                    }}>
                      UNSAVED
                    </span>
                  )}
                </div>
                {/* Whose claim is open. The editor writes to ONE facility's claim,
                    and an officer working four of them under one bureau needs that
                    on the panel they are typing into — not remembered from two
                    screens back. Named with its id, which is how facilities are
                    referred to in the XML and in support. */}
                <div style={G.cardSub}>
                  Bio &amp; insurance
                  {data?.header?.facility_name && (
                    <>
                      <span style={{ color: BRAND.inkFaint }}>{' · '}</span>
                      <span style={{ color: BRAND.navy, fontWeight: 800 }}>
                        {data.header.facility_name}
                        {data.header.facility_id != null && ` #${data.header.facility_id}`}
                      </span>
                    </>
                  )}
                </div>
              </span>

              {editing ? (
                <span style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                  <button type="button" style={G.editBtn} disabled={saving}
                    onClick={cancelEdit} title="Discard these changes">
                    <X size={12} /> Cancel
                  </button>
                  {/*  Held while the dates have changed the attendance and the
                       service is still the one from before the change.
                       BOTH cards' Save buttons are gated, not just the one under
                       the dates: they share `bio` and `saveBio`, so leaving the
                       other open would write the same mismatched claim by another
                       route. Saving in between produces a claim NHIA rejects, by
                       which time the officer is on the next one. */}
                  <button type="button" onClick={saveBio}
                    disabled={saving || serviceNeedsChoice}
                    title={serviceNeedsChoice
                      ? `These dates make this ${settingFromDates === 'outpatient'
                        ? 'an out-patient' : 'an in-patient'} claim — choose the `
                        + 'service it should be billed on first'
                      : (changed.length ? `Save ${changed.length} change(s)` : 'Nothing has changed')}
                    style={{
                      ...G.addBtn,
                      opacity: saving || serviceNeedsChoice ? .6 : 1,
                      cursor: saving ? 'wait' : (serviceNeedsChoice ? 'not-allowed' : 'pointer'),
                      background: changed.length && !serviceNeedsChoice ? BRAND.green : BRAND.inkFaint,
                      borderColor: changed.length && !serviceNeedsChoice ? BRAND.green : BRAND.inkFaint,
                    }}>
                    <Save size={12} /> {saving ? 'Saving…' : 'Save'}
                  </button>
                </span>
              ) : (
                <button type="button" style={G.editBtn} onClick={startEdit}>
                  <Pencil size={12} /> Edit
                </button>
              )}
            </header>
            <div style={{
              display: 'grid', gap: 10, padding: 14,
              gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
            }}>
              {/*  Read-only shows what the claim carries; editing shows the draft.
                   Every insurance identifier is editable — a wrong membership
                   number is one of the commonest reasons the NHIA rejects a claim,
                   and an officer who can see it but not correct it has to go back
                   to the source system to fix a typo.

                   ONE field stays fixed: the folder number is how the claim is
                   addressed, and editing the key you were found by orphans the row.

                   Note this differs from the reference screenshot, which had
                   MEMBERSHIP NO. showing the folder number (26/0253) and the policy
                   field showing the NHIS number. Bound to what each field actually
                   means here rather than reproducing that swap. */}
              <Field label="Insurance company" span={2} edit={editing}
                value={editing ? bio.scheme_name : h.scheme_name}
                onChange={(v) => setField('scheme_name', v)}
                options={(schemes.length ? schemes : [h.scheme_name].filter(Boolean))
                  .map((s) => ({ value: s, label: s }))} />
              <Field label="Membership no." edit={editing}
                value={editing ? bio.member_no : h.member_no}
                onChange={(v) => setField('member_no', v)} />
              <Field label="Folder number" edit={editing}
                value={editing ? bio.folder_no : h.folder_no}
                onChange={(v) => setField('folder_no', v)} />
              <Field label="Insurance policy number" edit={editing}
                value={editing ? bio.policy_option : h.policy_option}
                onChange={(v) => setField('policy_option', v)} />
              <Field label="Surname" edit={editing}
                value={editing ? bio.surname : h.surname}
                onChange={(v) => setField('surname', v)} />
              <Field label="Other names" edit={editing}
                value={editing ? bio.other_names : h.other_names}
                onChange={(v) => setField('other_names', v)} />
              <Field label="Gender" edit={editing}
                value={editing ? bio.gender : cap(h.gender)}
                onChange={(v) => setField('gender', v)}
                options={editing ? [
                  { value: 'male', label: 'Male' },
                  { value: 'female', label: 'Female' },
                  { value: 'other', label: 'Other' },
                ] : null} />
              {/* A real date input while editing, so the value can only ever be a
                  valid 'YYYY-MM-DD' — the flat column is VARCHAR and would happily
                  store "yesterday". Read-only shows the dd/mm/yyyy the design uses. */}
              <Field label="Date of birth" edit={editing} type="date"
                value={editing ? bio.date_of_birth : dmy(h.date_of_birth)}
                onChange={(v) => setField('date_of_birth', v)}
                hint={ageText(editing ? bio.date_of_birth : h.date_of_birth, h.visit_date)} />
              <Field label="CCC no" edit={editing}
                value={editing ? bio.ccc : h.ccc}
                onChange={(v) => setField('ccc', v)} />
              <Field label="Dependant" checkbox edit={editing}
                value={editing ? bio.is_dependant : h.is_dependant}
                onChange={(v) => setField('is_dependant', v)} />
            </div>
          </section>

          <section ref={refs.service} style={{
            ...G.card, scrollMarginTop: 8,
            borderLeftWidth: editing ? 4 : 3,
            boxShadow: editing ? `0 0 0 1px ${BRAND.green}` : 'none',
          }}>
            <header style={{
              ...G.cardHead,
              background: editing ? BRAND.greenSoft : undefined,
            }}>
              <span style={{
                ...G.cardIcon,
                background: editing ? '#D8EEE0' : BRAND.navySoft,
                color: editing ? BRAND.greenDark : BRAND.navy,
              }}><Activity size={15} /></span>
              <span style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}>
                  <span style={G.cardTitle}>Service &amp; dates</span>
                  {editing && changed.length > 0 && (
                    <span style={{
                      padding: '1px 8px', borderRadius: 20, fontSize: 9.5, fontWeight: 800,
                      letterSpacing: '.04em', background: '#F6E4C4', color: '#8A5510',
                    }}>
                      UNSAVED
                    </span>
                  )}
                </div>
                <div style={G.cardSub}>Attendance, outcome, stay</div>
              </span>

              {/*  Both cards edit ONE draft and share a Save, because they write to
                   the same row through the same endpoint. Two separate drafts would
                   let the second save silently discard the first. */}
              {editing ? (
                <span style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                  <button type="button" style={G.editBtn} disabled={saving}
                    onClick={cancelEdit} title="Discard these changes">
                    <X size={12} /> Cancel
                  </button>
                  {/*  Held while the dates have changed the attendance and the
                       service is still the one from before the change.
                       BOTH cards' Save buttons are gated, not just the one under
                       the dates: they share `bio` and `saveBio`, so leaving the
                       other open would write the same mismatched claim by another
                       route. Saving in between produces a claim NHIA rejects, by
                       which time the officer is on the next one. */}
                  <button type="button" onClick={saveBio}
                    disabled={saving || serviceNeedsChoice}
                    title={serviceNeedsChoice
                      ? `These dates make this ${settingFromDates === 'outpatient'
                        ? 'an out-patient' : 'an in-patient'} claim — choose the `
                        + 'service it should be billed on first'
                      : (changed.length ? `Save ${changed.length} change(s)` : 'Nothing has changed')}
                    style={{
                      ...G.addBtn,
                      opacity: saving || serviceNeedsChoice ? .6 : 1,
                      cursor: saving ? 'wait' : (serviceNeedsChoice ? 'not-allowed' : 'pointer'),
                      background: changed.length && !serviceNeedsChoice ? BRAND.green : BRAND.inkFaint,
                      borderColor: changed.length && !serviceNeedsChoice ? BRAND.green : BRAND.inkFaint,
                    }}>
                    <Save size={12} /> {saving ? 'Saving…' : 'Save'}
                  </button>
                </span>
              ) : (
                <button type="button" style={G.editBtn} onClick={startEdit}>
                  <Pencil size={12} /> Edit
                </button>
              )}
            </header>
            <div style={{
              display: 'grid', gap: 10, padding: 14,
              gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
            }}>
              {/*  SERVICE is a dropdown of the FACILITY's own service catalogue —
                   insured_services for an outpatient case, the inpatient catalogue
                   for an admission. Not the G-DRG tables: those are indexed by
                   diagnosis, and the diagnosis is its own fold further down.

                   Narrowed to the patient's age band, which the catalogue records
                   itself (Adult / Child / Both), so a nine-year-old is not offered
                   "General OPD - Adult". Picking one sets the code and the
                   inpatient/outpatient setting together, so the three cannot
                   disagree with each other. */}
              {/*  ── A LONG ADMISSION GETS A SEARCHABLE PICKER, NOT A <select> ──
                   The tariff book is 922 rows for an adult. A native dropdown of
                   that length is not a control, it is a scroll: "I want us to be
                   able to search for something like malaria… and a way we can click
                   and then only procedure will show, or only diagnosis."

                   SearchSelect already does the searching — code OR name, every
                   word in any order, exact code first — and four other panels in
                   this editor use it, so a code found here behaves like a code
                   found there. The only thing added is the PROCEDURE / DIAGNOSIS
                   filter above it, because on an admission those are two different
                   decisions and the list is far more useful once it is one of
                   them. */}
              {editing && serviceMode === 'principal' ? (
                <div style={{ gridColumn: 'span 2', display: 'grid', gap: 6 }}>
                  <div style={{
                    fontSize: 10.5, fontWeight: 800, letterSpacing: '.04em',
                    textTransform: 'uppercase', color: BRAND.inkMuted,
                  }}>Service — what this admission is billed on</div>

                  {/*  The kind filter. 'All' first, because an officer who knows
                       the code does not care which book it is in. */}
                  <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                    {[
                      ['all', 'All', services.length],
                      ['procedure', 'Procedures',
                        services.filter((o) => o.kind === 'procedure').length],
                      ['diagnosis', 'Diagnoses',
                        services.filter((o) => o.kind === 'diagnosis').length],
                    ].map(([k, label, n]) => {
                      const on = principalKind === k;
                      return (
                        <button key={k} type="button" onClick={() => setPrincipalKind(k)}
                          style={{
                            padding: '4px 10px', borderRadius: 20, cursor: 'pointer',
                            font: 'inherit', fontSize: 11.5, fontWeight: on ? 800 : 600,
                            border: `1px solid ${on ? BRAND.greenDark : BRAND.line}`,
                            background: on ? BRAND.greenSoft : '#fff',
                            color: on ? BRAND.greenDark : BRAND.inkSoft,
                          }}>
                          {label}
                          <span style={{
                            marginLeft: 5, fontWeight: 800, color: BRAND.inkFaint,
                          }}>{n.toLocaleString()}</span>
                        </button>
                      );
                    })}
                  </div>

                  <SearchSelect
                    options={principalKind === 'all'
                      ? services : services.filter((o) => o.kind === principalKind)}
                    value={bio.service_code || ''}
                    onChange={pickService}
                    placeholder="Search the tariff book — a code or a name"
                    searchPlaceholder="Type a code or a name, e.g. malaria…"
                    ariaLabel="What this admission is billed on"
                    //  Identity is the CODE AND THE NAME together. One G-DRG covers
                    //  several conditions — ORTH01A is Sprain, Strain, Tibial
                    //  Fracture and Varicose Veins — so keying on the code alone
                    //  would make four real choices select the first one.
                    idOf={(o) => `${o.kind}|${o.code}|${o.name}`}
                    primaryOf={(o) => `${o.code} — ${o.name}`}
                    secondaryOf={(o) => [
                      o.kind === 'procedure' ? 'Procedure' : 'Diagnosis',
                      o.specialty_code || null,
                      //  No price is said, not shown as 0.00 — not one of the 872
                      //  procedures in the book carries a tariff.
                      o.tariff == null ? 'no tariff in the book'
                        : `GHS ${Number(o.tariff).toFixed(2)}`,
                    ].filter(Boolean).join(' · ')}
                  />
                  <div style={{ fontSize: 10.5, color: BRAND.inkMuted, lineHeight: 1.5 }}>
                    {serviceNote}
                  </div>
                </div>
              ) : (
              <Field label="Service" span={2} edit={editing}
                //  Falls back to the CODE when the catalogue has no name for it.
                //  Returning '' left the field blank on an imported claim whose
                //  service code is perfectly well known — the value was on screen
                //  twice over in "Service code" and "Specialty GDRG" beside it.
                value={editing ? bio.service_code : (() => {
                  const name = h.service_name || h.service_code;
                  if (!name) return '';
                  return `${name}${h.attendance_type ? ` (${cap(h.attendance_type)})` : ''}`;
                })()}
                onChange={pickService}
                options={editing
                  ? [
                      { value: '', label: 'Select Service' },
                      // The service name alone. The tariff is not what an officer
                      // picks a service by, and it made every row longer than the
                      // field is wide.
                      ...services.map((o) => ({ value: o.code, label: o.label })),
                      // The claim's current service, if this catalogue or the age
                      // filter excludes it — otherwise opening the card would
                      // silently blank a field the officer never touched.
                      ...(bio.service_code && !services.some((o) => o.code === bio.service_code)
                        ? [{ value: bio.service_code,
                             label: `${bio.service_name || bio.service_code} (current)` }]
                        : []),
                    ]
                  : null}
                hint={!editing ? null : (serviceNote
                  || `${services.length} ${serviceBand || settingForList} service(s)`
                     + `${serviceBand ? ` — age ${ageYears ?? '?'}` : ''}`
                     + `${serviceSource ? ` · ${serviceSource}` : ''}`)} />
              )}

              {/*  Follows from the service; never typed. */}
              <Field label="Service code" locked edit={editing}
                value={editing ? bio.service_code : h.service_code} />

              {/*  Fixed. It decides which month the claim is counted in, and the
                   admission is the same day on essentially every claim. */}
              <Field label="Visit date" locked edit={editing} value={h.visit_date} />

              <Field label="Service outcome" edit={editing}
                value={editing ? bio.outcome : cap(h.outcome)}
                onChange={(v) => setField('outcome', v)}
                options={editing ? [
                  { value: '', label: 'Select Outcome' },
                  { value: 'discharged', label: 'Discharged' },
                  { value: 'died', label: 'Died' },
                  { value: 'transferred_out', label: 'Transferred Out' },
                  // Only offered when it is already the value — the reference design
                  // lists three outcomes, and "pending" is an importer default.
                  ...(bio.outcome === 'pending'
                    ? [{ value: 'pending', label: 'Pending' }] : []),
                ] : null} />

              {/*  The NHIA typeOfAttendance code — why the patient came. A different
                   thing from inpatient-vs-outpatient, which the SERVICE sets. */}
              <Field label="Type of attendance" edit={editing}
                value={editing
                  ? bio.attendance_code
                  : (ATTENDANCE_LABEL[h.attendance_code] || h.attendance_code
                     || cap(h.attendance_type))}
                onChange={(v) => setField('attendance_code', v)}
                options={editing ? [
                  { value: '', label: 'Select Attendance' },
                  //  CFU, not CHR — the code NHIA accepts. This dropdown is what
                  //  writes the value back, so leaving CHR here would have
                  //  re-introduced the rejection on every claim an officer
                  //  touched. See ATTENDANCE_CODES in claimStore.js.
                  { value: 'CFU', label: 'Chronic follow-ups' },
                  { value: 'EAE', label: 'Emergency/Acute' },
                  { value: 'ANC', label: 'Antenatal' },
                  { value: 'PNC', label: 'Postnatal' },
                ] : null} />

              {/*  Fixed, being the visit date. */}
              <Field label="Admission date" locked edit={editing}
                value={dmy(h.admission_date)} />

              {/*  The one date that moves — this is what a longer stay changes. */}
              <Field label="Discharge date" edit={editing} type="date"
                value={editing ? bio.discharge_date : dmy(h.discharge_date)}
                onChange={setDischarge}
                //  The stay length is shown in BOTH modes. It used to appear only
                //  while editing, so a claim you were merely reading gave no hint
                //  of how long the admission was — the one fact the discharge date
                //  exists to convey.
                hint={(() => {
                  const adm = h.admission_date || h.visit_date;
                  const d = editing ? bio.discharge_date : h.discharge_date;
                  if (!d) return editing ? 'Leave blank if not discharged' : 'not recorded';
                  if (!adm) return null;
                  const n = Math.round((new Date(d) - new Date(adm)) / 86400000);
                  if (n < 0) return 'Before the visit date';
                  if (n === 0) return 'Same day — outpatient';
                  // The consequence, said at the moment it happens rather
                  // than in the confirmation after the fact.
                  return `${n} night${n === 1 ? '' : 's'} stay — INPATIENT`;
                })()} />

              {/*  ── THE ATTENDANCE JUST CHANGED, SO THE SERVICE MUST TOO ──────
                   Setting the discharge date equal to the admission date is how an
                   admission is corrected to a day case. The claim becomes an
                   out-patient one at that instant — and the service it is billed
                   on, PAED39C here, is an in-patient code that NHIA will reject on
                   an out-patient claim.

                   Said HERE, under the date that caused it, and while the card is
                   still open. The alternative is saving, reading the banner, and
                   coming back — which is the round trip this removes.

                   Save is blocked until a service is chosen, because a claim saved
                   in between is one NHIA rejects, and nothing on the screen would
                   yet be pointing at why. */}
              {editing && (flippedToOutpatient || flippedToInpatient) && (
                <div style={{
                  gridColumn: '1 / -1',
                  marginTop: 2, padding: '10px 12px', borderRadius: 8,
                  background: '#FFF4E5', border: '1px solid rgba(154,91,0,.30)',
                  color: '#7A4A00', fontSize: 12, lineHeight: 1.5,
                }}>
                  <strong>
                    {flippedToOutpatient
                      ? 'These dates make this an OUT-PATIENT claim.'
                      : 'These dates make this an IN-PATIENT claim.'}
                  </strong>{' '}
                  {flippedToOutpatient
                    ? 'It is billed on an in-patient service, which cannot stay. '
                    : 'It is billed on an out-patient service, which cannot stay. '}
                  Pick the {flippedToOutpatient ? 'out-patient' : 'in-patient'} service
                  it should be billed on — the list above has reloaded for it, and
                  that choice becomes the claim's principal G-DRG.
                  {serviceNeedsChoice && (
                    <div style={{ marginTop: 6, fontWeight: 700 }}>
                      Save is held until a service is chosen.
                    </div>
                  )}
                </div>
              )}

              {/*  ── WHERE THESE THREE ACTUALLY COME FROM ─────────────────────
                   All three used to say "From the service above". Two of them do
                   come from the service. THE SPECIALTY CODE DOES NOT, on an
                   imported claim, and that label is what made a real value look
                   impossible:

                     Service      MEDI28A
                     Specialty    D001          <- "From the service above"

                   D001 cannot be derived from anything. claimRules.groupOf takes
                   the first four characters only when they are four LETTERS, so it
                   returns null for D001 — the system's own definition of a specialty
                   group cannot produce it. It was never derived; it was STORED.

                   `claims.specialty_gdrg_code` is filled on import straight from the
                   XML's <specialtyAttended> element — see nhis-claim-xml.js. It is
                   WHERE THE PATIENT WAS SEEN, which is a different question from what
                   the claim is billed on, and the two legitimately differ on 138 of
                   2,383 imported claims: MEDI and OPDC visits billing ZOOM detention
                   codes, ENTH and DENT visits billing OPDC ones.

                   So the field is not wrong to differ. The LABEL was wrong to promise
                   it would not. Each now says where its own value came from. */}
              <Field label="Specialty GDRG" locked edit={editing}
                value={editing ? bio.service_code : h.service_code}
                hint="From the service above" />
              {/*  ── AND WHY IT USED TO BE BLANK ──────────────────────────────
                   Not "sometimes", as it was reported — on every claim raised at
                   the desk. detailFlat read `spe_cat` alone, and nothing in the
                   OPD -> doctor flow writes that column; only the claims panel
                   does. It is now resolved from the facility's own catalogues the
                   way the imported side has always resolved it, so this field is
                   filled on READ and the note below only appears when the value
                   was also WRITTEN back to the claim. */}
              <Field label="Specialty description" locked edit={editing}
                value={editing ? bio.specialty_name : h.specialty_name}
                hintTone={settling ? 'busy' : (settledFor('specialty_name') ? 'ok' : undefined)}
                hint={settling
                  ? 'Filling this in…'
                  : (settledFor('specialty_name')
                    ? 'Filled in automatically, from this facility’s catalogue'
                    : 'From the service above')} />
              {/*  ── THE HINT USED TO EXCUSE THIS FIELD ─────────────────────
                   It said "where the patient was seen — from the imported file,
                   not the service", which was true of the provenance and, since
                   the facility overruled the old rule on 2026-09-02, wrong about
                   whether that is acceptable. The specialty now FOLLOWS the
                   service, so a disagreement is a fault the officer should act
                   on — and the field was reassuring them while the issue banner
                   above said the opposite about the same claim.

                   Now it reports the rule's own verdict, and names the value it
                   should be. */}
              {/*  ── THE HINT NO LONGER SENDS THEM TO A DEAD BUTTON ───────────
                   It said "Use Repair to correct it" on every claim. On a HOSPITAL
                   claim that button cannot act — the repair endpoint reads
                   `source: 'imported'` and takes integer ids, and a flat claim is
                   addressed by 'patientId::date'. So the officer was told to press
                   something that answered "No imported claim with this id."

                   The correction now happens on open, and the field reports it:
                   busy while it runs, then the two values it moved between. The
                   "Use Repair" wording survives only where Repair genuinely
                   works — the imported side, which is left exactly as it was. */}
              <Field label="Specialty code" locked edit={editing}
                value={editing ? bio.specialty_code : h.specialty_code}
                hintWarn={!!specialtyIssue && !settling && !settledFor('specialty_code')}
                hintTone={settling ? 'busy' : (settledFor('specialty_code') ? 'ok' : undefined)}
                hint={settling
                  ? 'Correcting the specialty…'
                  : settledFor('specialty_code')
                    ? `Corrected automatically: ${settledFor('specialty_code').from || '—'} → `
                      + `${settledFor('specialty_code').to}`
                      + `${settledFor('specialty_code').why
                        ? ` (${settledFor('specialty_code').why})` : ''}`
                    : specialtyIssue
                      ? (specialtyIssue.suggested && specialtyIssue.suggested.to
                        ? `Should be ${specialtyIssue.suggested.to} — `
                          + `${specialtyIssue.suggested.why}.`
                          //  NO LONGER "Use Repair to correct it." The correction
                          //  runs on open now, on both models, so by the time this
                          //  text could be read it has already happened — and on
                          //  the one claim the facility kept sending, that
                          //  instruction pointed at a button that had nothing to
                          //  act on. It shows only where the fix did NOT apply,
                          //  which means the rule offered no answer.
                        : specialtyIssue.title)
                      : specialtyFromFile
                        ? 'From the imported file, and it agrees with the service'
                        : 'From the service above'} />

              {/*  Patient status IS the outcome — the same column, shown twice by
                   the reference design. Read-only so there is one place to set it. */}
              <Field label="Patient status" locked edit={editing}
                value={cap(editing ? bio.outcome : h.patient_status)} />
            </div>
          </section>
        </div>

        {/* Procedures / Diagnoses / Investigations */}
        <div>
          <Fold ref={refs.procedures} openToken={openToken} focused={focus === 'procedures'}
            wantOpen={wants('procedures', data?.procedures?.length)}
            icon={Stethoscope} title="Procedures" count={data?.procedures?.length}
            tone="procedures"
            notReady={notReady}
            //  One Add, in the header where the design puts it. It bumps a
            //  counter the table watches rather than reaching into the child
            //  with a ref — the fold has to open first, and a ref into a
            //  not-yet-mounted child would be null on the first click.
            onAdd={() => setAddProcedure(true)}>
            {/*  Rows are added, filled in and saved one at a time, on either
                 model. Where they LAND differs — an imported claim's procedures
                 go to claim_procedures, a claim raised here goes to
                 doctors_procedure_ keyed by patient and visit date — and
                 claimStore decides which from the claim's own source. The editor
                 does not need to know. */}
            <ClaimProcedures
              claimId={claimId}
              source={data?.source}
              scheme={data?.header?.scheme_name}
              age={ageYears}
              rows={data?.procedures || []}
              readOnly={false}
              // So the ICD-10 on a procedure can be the one the patient was
              // actually diagnosed with, rather than the first of thirty-four.
              diagnoses={data?.diagnoses || []}
              addSignal={addProcedure}
              onAddConsumed={() => setAddProcedure(false)}
              onSaved={(fresh) => { setData(fresh); if (onChanged) onChanged(fresh); }} />
          </Fold>

          <Fold ref={refs.diagnoses} openToken={openToken} focused={focus === 'diagnoses'}
            wantOpen={wants('diagnoses', data?.diagnoses?.length)}
            icon={FileText} title="Diagnoses" count={data?.diagnoses?.length}
            tone="diagnoses"
            notReady={notReady}
            onAdd={() => setAddDiagnosis(true)}>
            {/*  Same rhythm as Procedures. A typed-in claim's diagnoses live in
                 doctors_analysis, keyed by patient and date, so the table is
                 read-only there rather than offering an Add that cannot save. */}
            <ClaimDiagnoses
              claimId={claimId}
              source={data?.source}
              scheme={data?.header?.scheme_name}
              age={ageYears}
              rows={data?.diagnoses || []}
              //  A DIAGNOSIS CANNOT BE THE PRINCIPAL ON AN OUT-PATIENT CLAIM.
              //  An OPD attendance is billed on its service, or on a procedure if
              //  one was done — never on a diagnosis. So the star column is
              //  disabled there rather than offering a choice that cannot exist.
              //
              //  `settingForList` and not the stored attendance_type, so it follows
              //  the DRAFT: extending the discharge date turns an attendance into an
              //  admission, and the star has to become available in the same edit
              //  rather than after a save and a reload.
              setting={settingForList}
              //  A procedure outranks every diagnosis, so with one present the
              //  principal is chosen in the Procedures table and the star here
              //  goes dead rather than offering a losing answer.
              procedureCount={(data?.procedures || []).length}
              readOnly={false}
              addSignal={addDiagnosis}
              onAddConsumed={() => setAddDiagnosis(false)}
              onSaved={(fresh) => { setData(fresh); if (onChanged) onChanged(fresh); }} />
          </Fold>

          <Fold ref={refs.investigations} openToken={openToken}
            focused={focus === 'investigations'}
            wantOpen={wants('investigations', data?.investigations?.length)}
            icon={FlaskConical} title="Investigations" count={data?.investigations?.length}
            tone="investigations"
            notReady={notReady}
            onAdd={() => setAddInvestigation(true)}>
            {/*  lab_prices + scan_price, the tables the claims panel's /loadLabs
                 reads. Read-only on a typed-in claim, whose investigations live
                 in the `investigation` table keyed by patient and date. */}
            <ClaimInvestigations
              claimId={claimId}
              source={data?.source}
              scheme={data?.header?.scheme_name}
              rows={data?.investigations || []}
              readOnly={false}
              addSignal={addInvestigation}
              onAddConsumed={() => setAddInvestigation(false)}
              onSaved={(fresh) => { setData(fresh); if (onChanged) onChanged(fresh); }} />
          </Fold>
        </div>

        {/*  Medications, as a Fold like the other three. It used to be a
             hand-built <section> with its own open/closed state, its own chevron
             and its own header — which is why it did not behave like them: the
             auto-open rule, the focus highlight and the Entries navigation all
             lived in Fold and this one reimplemented two of the three. */}
        <div>
          <Fold ref={refs.medications} openToken={openToken}
            focused={focus === 'medications'}
            wantOpen={wants('medications', meds.length)}
            icon={Pill} title="Medications" count={meds.length}
            tone="medications"
            notReady={notReady}
            onAdd={() => setAddMedication(true)}>
            {/*  store_pro, the table /loadMedicationsCalim reads. Read-only on a
                 typed-in claim, whose medications live in the `medication` table
                 keyed by patient and date. */}
            <ClaimMedications
              claimId={claimId}
              source={data?.source}
              scheme={data?.header?.scheme_name}
              rows={meds}
              readOnly={false}
              // So a learned prescription is one given to somebody of the same age.
              age={ageYears}
              addSignal={addMedication}
              onAddConsumed={() => setAddMedication(false)}
              onSaved={(fresh) => { setData(fresh); if (onChanged) onChanged(fresh); }} />
          </Fold>
        </div>
      </div>

      {/* ══ SIDEBAR ═══════════════════════════════════════════════════════
          Sticky, because Entries is navigation: a nav that scrolls away with the
          content it navigates is no use past the first section. `top` clears the
          table's sticky column header. */}
      {/*  The aside STRETCHES to the full grid row and its inner wrapper is what
           sticks. That distinction is the whole trick: a sticky element can only
           travel inside its own box, so a start-aligned aside shrinks to its
           content and the nav scrolls away after a few hundred pixels. Stretching
           the outer box and sticking the inner one gives it the full height of the
           panel to stay put across. */}
      <aside style={{ minWidth: 0, alignSelf: 'stretch' }}>
      <div style={{ display: 'grid', gap: 12, minWidth: 0 }}>
        {/*  Only the ENTRIES card sticks — not the whole sidebar.
             Sticky positioning cannot hold an element taller than the scrollport:
             it pins until the element's bottom is reached and then scrolls like
             anything else. The sidebar stack was well over a screen tall, so
             sticking the wrapper left the nav scrolling away and whatever sat
             under it arriving at the top instead. The nav is short, so it can
             genuinely stay.

             Still true with the assistant and Comments panels gone: Entries plus
             the Cost summary can exceed a short window on their own.

             maxHeight + its own scroll is the second half: on a claim with twenty
             specialties, or a short window, the card scrolls INSIDE itself rather
             than growing past the viewport and taking the whole trick down. */}
        <section style={{
          ...G.sideCard,
          position: 'sticky', top: stickyTop + 8, zIndex: 1,
          maxHeight: `calc(100vh - ${stickyTop + 150}px)`,
          overflowY: 'auto',
          scrollbarWidth: 'thin',
        }}>
          <div style={{ ...G.sideHead, position: 'sticky', top: 0, zIndex: 1 }}>
            <span>Entries</span>
            {/* Releases the focus so everything with content comes back. Only
                offered while a section is focused — otherwise it would suggest
                something is hidden when nothing is. */}
            {focus && (
              <button type="button" onClick={() => setFocus(null)}
                title="Show every section that has content"
                style={{
                  padding: '2px 9px', borderRadius: 6, font: 'inherit', fontSize: 10.5,
                  fontWeight: 800, cursor: 'pointer', border: `1px solid ${BRAND.line}`,
                  background: BRAND.surface, color: BRAND.inkMuted,
                }}>
                Show all
              </button>
            )}
          </div>
          <div style={{ padding: 7, display: 'grid', gap: 3 }}>
            {entries.map((e) => {
              // Highlighted when it is the focused section. With nothing focused
              // nothing is highlighted, which is honest: you are looking at
              // everything the claim has, not at one section.
              const active = focus === e.key;
              // Past History has no section on this claim yet, so it navigates
              // nowhere and says so rather than doing nothing silently.
              const navigable = !!refs[e.key];
              //  The same tone the section itself carries, so this list teaches the
              //  mapping rather than being a second colour scheme to learn. Entries
              //  with no section — Patient Bio, Past History — keep the neutral.
              const eTone = TONE[e.key] || null;
              return (
                <div key={e.label}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 9,
                    borderRadius: 7, fontSize: 12, fontWeight: 650,
                    background: active ? (eTone ? eTone.wash : BRAND.greenSoft) : 'transparent',
                    color: active ? (eTone ? eTone.ink : BRAND.greenDark) : BRAND.ink,
                    // A left edge on the active entry, matching the section's own —
                    // the wash alone is too faint to read as "you are here".
                    boxShadow: active && eTone ? `inset 3px 0 0 ${eTone.line}` : 'none',
                  }}>
                  <button
                    type="button"
                    //  Two entries are not sections of THIS claim and never will
                    //  be, so they open rather than scroll. Past History is the
                    //  patient's OTHER attendances; Documentation is the clinical
                    //  note behind this visit, which lives in the consulting
                    //  room's record and not on the claim form. Both used to open
                    //  the "not wired up yet" box.
                    onClick={() => {
                      if (e.key === 'history') { setHistoryOpen(true); return; }
                      if (e.key === 'documentation') { setDocsOpen(true); return; }
                      return navigable ? goTo(e.key) : notReady(e.label);
                    }}
                    aria-current={active ? 'true' : undefined}
                    title={e.key === 'history'
                      ? 'Every other attendance recorded for this patient'
                      : e.key === 'documentation'
                        ? 'Complaint, history, examination and other notes for this visit'
                        : (navigable ? `Go to ${e.label}` : `${e.label} is not on this claim yet`)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 9, flex: 1, minWidth: 0,
                      padding: '7px 9px', border: 0, background: 'none', font: 'inherit',
                      fontWeight: 'inherit', color: 'inherit', textAlign: 'left',
                      cursor: 'pointer', borderRadius: 7,
                    }}
                  >
                    {/*  Tinted even when NOT active, so the four sections are
                         recognisable in this list at rest — that is what makes the
                         icon-to-colour pairing something you learn once rather than
                         only see on the one row you happen to be standing on. */}
                    <e.icon size={14} style={{
                      color: active
                        ? (eTone ? eTone.ink : BRAND.greenDark)
                        : (eTone ? eTone.line : BRAND.inkMuted),
                      flexShrink: 0 }} />
                    <span style={{ flex: 1, minWidth: 0 }}>{e.label}</span>

                    {/*  ── WHAT THIS CLAIM ALREADY HAS ─────────────────────────
                         Documentation is three separate pieces of work, so one
                         count would not say anything useful — "2" tells nobody
                         whether the notes or the lab results are the ones still
                         outstanding. Three marks, each solid when its own piece
                         is done and hollow when it is not, so the row can be read
                         without opening anything.

                         Labs carry a fraction rather than a tick because part-way
                         is the normal state on a claim with several tests, and a
                         tick would round it up to finished. */}
                    {e.key === 'documentation' && docStatus && (
                      <span style={{ display: 'inline-flex', gap: 3, alignItems: 'center' }}
                        title={docStatusTitle(docStatus)}>
                        {docMarks(docStatus).map((m) => (
                          <span key={m.key} style={{
                            display: 'inline-flex', alignItems: 'center', gap: 2,
                            padding: '1px 5px', borderRadius: 20,
                            fontSize: 9.5, fontWeight: 800, letterSpacing: .2,
                            border: `1px solid ${m.done ? 'rgba(30,107,57,.28)' : BRAND.line}`,
                            background: m.done ? '#E8F5EC' : '#F6F8FB',
                            color: m.done ? '#1E6B39' : BRAND.inkFaint,
                          }}>{m.label}</span>
                        ))}
                      </span>
                    )}

                    {e.count != null && (
                      <span style={{
                        minWidth: 20, textAlign: 'center', padding: '1px 6px', borderRadius: 20,
                        fontSize: 10.5, fontWeight: 800,
                        background: e.count ? BRAND.navySoft : '#EEF1F6',
                        color: e.count ? BRAND.navy : BRAND.inkFaint,
                      }}>{e.count}</span>
                    )}
                  </button>
                  {e.action === 'add' && (
                    <button type="button" aria-label={`Add ${e.label}`}
                      //  All four sections are built, on both models, so the +
                      //  brings the section into view and adds a row. Anything
                      //  this shortcut does not know how to open still says so
                      //  rather than pretending.
                      onClick={() => {
                        const wired = { procedures: setAddProcedure,
                                        diagnoses: setAddDiagnosis,
                                        investigations: setAddInvestigation,
                                        medications: setAddMedication }[e.key];
                        if (!wired) {
                          notReady(`Adding a ${e.label.toLowerCase()}`);
                          return;
                        }
                        goTo(e.key);
                        wired(true);
                      }}
                      style={{ border: 0, background: 'none', cursor: 'pointer',
                        color: BRAND.inkMuted, padding: '2px 8px 2px 2px' }}>
                      <Plus size={13} />
                    </button>
                  )}
                  {e.action === 'edit' && (
                    <button type="button" aria-label={`Edit ${e.label}`}
                      onClick={() => notReady(`Editing ${e.label.toLowerCase()}`)}
                      style={{ border: 0, background: 'none', cursor: 'pointer',
                        color: BRAND.inkMuted, padding: '2px 8px 2px 2px' }}>
                      <Pencil size={13} />
                    </button>
                  )}
                </div>
              );
            })}
          </div>

        </section>

        {/* Cost summary */}
        <section style={G.sideCard}>
          <div style={G.sideHead}>Cost summary (insurance unit)</div>
          <div style={{ padding: '4px 0' }}>
            {[
              ['Diagnosis', t.diagnoses, null],
              ['Procedure', t.procedures, null],
              ['Service', t.services, h.service_name],
              ['Medication', t.medications, null],
              ['Investigation', t.investigations, null],
            ].map(([label, value, note]) => (
              <div key={label} style={{
                display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
                gap: 10, padding: '6px 13px', fontSize: 12,
              }}>
                <span style={{ minWidth: 0 }}>
                  <span style={{ color: BRAND.ink }}>{label}</span>
                  {note && (
                    <span style={{
                      display: 'block', fontSize: 10.5, color: BRAND.inkFaint,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      maxWidth: 168,
                    }}>{note}</span>
                  )}
                </span>
                <span style={{ fontVariantNumeric: 'tabular-nums', color: BRAND.inkMuted,
                  whiteSpace: 'nowrap' }}>
                  {ghs(value)}
                </span>
              </div>
            ))}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              gap: 10, padding: '9px 13px', marginTop: 3,
              borderTop: `1px solid ${BRAND.line}`, fontSize: 13, fontWeight: 800,
            }}>
              <span>Grand</span>
              <span style={{ fontVariantNumeric: 'tabular-nums', color: BRAND.greenDark }}>
                {ghs(t.grand)}
              </span>
            </div>
            <div style={{ padding: '0 10px 10px' }}>
              <button type="button"
                style={{ ...G.ghost, background: BRAND.ink, color: '#fff', borderColor: BRAND.ink }}
                onClick={() => notReady('Detailed report')}>
                <BarChart3 size={13} /> Detailed report
              </button>
            </div>
          </div>
        </section>

        {onClose && (
          <button type="button" onClick={onClose} style={G.ghost}>
            Close this claim
          </button>
        )}
      </div>
      </aside>

      {/*  Entries ▸ Past History. Mounted at the editor's root, not inside the
           Entries list, so opening it does not depend on that list staying
           rendered — and so it sits above the whole panel rather than inside a
           column with its own scroll.

           `folder_no` is the patient's id in this system: it is what the flat
           model calls patient_id and what every clinical table keys on, which is
           why the consulting room's endpoint accepts it unchanged. */}
      <PastHistoryDialog
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        patientId={data?.header?.folder_no || null}
        patientName={[data?.header?.surname, data?.header?.other_names]
          .filter(Boolean).join(' ') || patientName || null}
        //  So the row for the visit THIS claim bills is marked in the list
        //  rather than left to be matched by eye.
        currentVisit={data?.header?.admission_date || data?.header?.visit_date || null}
        //  The dialog resolves the history from the CLAIM now, not from the
        //  folder number alone — an imported member has no registered visits, and
        //  their attendances are their other claims.
        claimId={claimId}
        claimSource={source} />

      {/*  Entries ▸ Documentation. Same identifier as Past History, for the same
           reason — `folder_no` is what the clinical tables key on.

           The DATE, though, is deliberately not the same expression. Past History
           lists every attendance and only needs to know which row to mark, so
           admission_date-or-visit_date is fine there. Here the date is the KEY the
           note is written under, and documentation belongs to the day the patient
           was SEEN. For an admission those two diverge, which is one more reason
           the dialog treats in-patients as out of scope for now. */}
      <DocumentationDialog
        open={docsOpen}
        onClose={() => setDocsOpen(false)}
        patientId={data?.header?.folder_no || null}
        patientName={[data?.header?.surname, data?.header?.other_names]
          .filter(Boolean).join(' ') || patientName || null}
        //  Straight from the header, not from `bio`: the draft only carries the
        //  discharge date — the visit date is fixed and has no editable copy.
        //  visit_date first, because documentation belongs to the day the patient
        //  was SEEN; the two are the same value on a flat claim anyway.
        visitDate={String(data?.header?.visit_date
          || data?.header?.admission_date || '').slice(0, 10) || null}
        //  `settingForList`, not header.attendance_type: it is already normalised
        //  to inpatient/outpatient AND derived from the draft dates, so a claim
        //  being corrected from an admission to a day case is judged by what the
        //  officer has just typed rather than by what it was imported as. The raw
        //  header value is 'Out-Patient' on one model and 'outpatient' on the
        //  other, which is a second reason not to read it here.
        attendance={settingForList}
        //  For Generate. The same pair the editor uses to fetch the claim, so
        //  the backend re-reads exactly this claim — nothing about its contents
        //  is sent from the browser.
        claimId={claimId}
        claimSource={source}
        //  Decides whether the Lab results tab appears at all. Counted here from
        //  the claim already loaded rather than by asking the server, so opening
        //  Documentation on a claim with no investigations costs no request.
        investigationCount={(data?.investigations || []).length} />
    </div>
  );
}
